import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import {
  PiAddressBook,
  PiBarcode,
  PiBell,
  PiBuildings,
  PiCaretDown,
  PiCaretRight,
  PiConfetti,
  PiCopy,
  PiDownloadSimple,
  PiEnvelope,
  PiFileText,
  PiGear,
  PiHourglass,
  PiMapTrifold,
  PiPackage,
  PiPaperclip,
  PiPencilSimple,
  PiProhibit,
  PiPaperPlaneRight,
  PiPlus,
  PiPulse,
  PiPushPin,
  PiPushPinSlash,
  PiNotepad,
  PiPause,
  PiPlay,
  PiSparkle,
  PiTray,
  PiUser,
  PiTrash,
  PiUploadSimple,
  PiXCircle,
} from 'react-icons/pi'
// eslint-disable-next-line no-restricted-imports -- Phosphor's ellipsis glyphs render too thin; lucide Ellipsis is the approved overflow icon.
import { Ellipsis } from 'lucide-react'
import {
  ActivityComposer,
  ActivityTimeline,
  type ActivityItem,
  type ActivityType,
  AddPill,
  Backdrop,
  Badge,
  type BadgeColor,
  Button,
  Checkbox,
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  DetailHeader,
  DetailPanel,
  DetailSection,
  Dialog,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EditableInfoRow,
  type EditableSelectOption,
  EntityDetailRail,
  EntityPreviewCard,
  EntityRow,
  EntitySummaryCard,
  EntitySummaryRow,
  FilterChip,
  HelpPopover,
  Popover,
  PopoverContent,
  PopoverTrigger,
  InfoList,
  InfoRow,
  Input,
  MultiCombobox,
  type MultiComboboxOption,
  MultiSelectFilter,
  OverlayWrapper,
  PeriodPicker,
  type PeriodQuickSelect,
  type PeriodValue,
  Radio,
  RadioGroup,
  type RichTextCommand,
  RichTextEditor,
  SearchBar,
  Skeleton,
  StageTracker,
  type StageTrackerStage,
  StatusStat,
  TabContent,
  TabList,
  TabRoot,
  TabTrigger,
} from '@firstbite/ui'
import useApiService from 'services/api'
import type { ApplyTo, DealDetail, DealEdit } from 'models/deals'
import type { SalesStage } from 'models/sales_stages'
import type { ContactCompany } from 'models/companies'
import type { CaptureForm } from 'models/captureForms'
import type { ActivityV2 } from 'models/activityV2'
import type { EmailMessage } from 'models/emailIntegration'
import type { INote } from 'models/contact_companies'
import type { ReminderListItem, ReminderStatus } from 'models/reminders'
import {
  getDocuments,
  useDeleteDocument,
  type Document,
} from 'services/documents'
import { formatBytes } from 'utils/formatBytes'
import { reminderTypeFormat } from 'components/Reminders/utils'
import { useDealRecommendationHistory } from './useDealRecommendationHistory'
import type { RecommendationHistoryItem } from 'models/recommendationHistory'
import ReminderModal from 'components/Reminders/ReminderModal'
import { UploadDocumentModal } from 'components/DocumentLibrary/UploadDocumentModal'
import { getActionText } from 'components/NewCompanyPage/EventHistory/EventCards/BaseEventCard'
import { buildEmailFromEvent } from 'components/NewCompanyPage/EventHistory/utils'
import CellEventFormatterV2 from 'components/Tables/ActivityTableV2/CellEvent/CellEventFormatterV2'
import { TimelineEmail } from 'components/EntityDetailPage/TimelineEmail'
import { DeleteIconButton } from 'components/EntityDetailPage/DeleteIconButton'
import { freshnessColor } from 'components/EntityDetailPage/freshness'
import { useNoteMentions } from 'components/EntityDetailPage/useNoteMentions'
import { useNoteEditAttachments } from 'components/EntityDetailPage/useNoteEditAttachments'
import { TagPill } from 'components/EntityDetailPage/TagPill'
import { useConfirmDialog } from 'components/EntityDetailPage/useConfirmDialog'
import { documentIcon } from 'components/EntityDetailPage/documentIcon'
import { useUserOptions, useUsers } from 'services/users'
import { useGatedStageMap } from 'services/captureForms'
import { getApiErrorMessage } from 'utils/getApiErrorDetail'
import {
  RICH_TEXT_EXEC,
  activeRichTextCommands,
  escapeHtml,
} from 'utils/richTextComposer'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { usdFormatterOrFallback } from 'utils/formatting'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { usePinTarget } from 'components/QuickAccess/usePinTarget'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import {
  getStageColorHex,
  getStageColorKey,
  getStageKind,
  isTerminalStage,
} from 'components/SalesStage/stageColors'
import { CaptureFormModal } from 'components/CaptureForms/CaptureFormModal'
import { EmailsModal } from 'components/NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { SelectLocationsPanelFbui } from 'components/Deals/CreateDealModal/SelectLocationsPanelFbui'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import { NotFoundContent } from '../not-found-content'

// ─── helpers ────────────────────────────────────────────────────────────────

/** A single-field PATCH of the deal record (mirrors the EditDeal payload). */
type DealPatch = Partial<DealEdit>

function fmtDate(value?: string | null): string {
  if (!value) return '—'
  return dayjs(value).format('MMM D, YYYY')
}

/** "N location" / "N locations" — pluralizes the door/location count. */
function locationsLabel(n: number): string {
  return `${n.toLocaleString()} location${n === 1 ? '' : 's'}`
}

/** Plain-text preview of a note's rich-text (HTML) content. */
function stripHtml(html: string): string {
  const el = document.createElement('div')
  el.innerHTML = html
  return (el.textContent ?? '').trim()
}

/** The note content-type models that render as a "note" timeline item. */
const NOTE_MODELS = new Set([
  'dealnote',
  'companynote',
  'contactnote',
  'doornote',
])

/** A field value from an activity event's `field_history_objects`. */
function eventField(event: ActivityV2, name: string): unknown {
  return event.field_history_objects?.find((f) => f.field_name === name)?.value
}

/**
 * Map a real deal activity event ({@link ActivityV2}) to an fb-ui
 * {@link ActivityItem}. Covers the main content types (note / email / reminder /
 * document / capture) with a generic field-change fallback — a lighter reprise
 * of the legacy EventCards, enough for the timeline's summary + icon + detail.
 */
function dealEventToItem(
  event: ActivityV2,
  stages: SalesStage[],
  customOrderEnabled: boolean,
  onViewThread: (event: ActivityV2) => void
): ActivityItem {
  const model = event.content_type.model
  const actor = event.user_name || 'First Bite'
  const base = { id: event.id, timestamp: event.created_at, actor }

  if (NOTE_MODELS.has(model)) {
    const content = eventField(event, 'content')
    return {
      ...base,
      type: 'note',
      summary: getActionText(event.action_type, 'note'),
      detail: content ? stripHtml(String(content)) : undefined,
    }
  }

  if (model === 'emailmessage') {
    const subject = (eventField(event, 'subject') as string) || '(No subject)'
    const snippet = (eventField(event, 'snippet') as string) || ''
    const outbound = Boolean(eventField(event, 'outbound'))
    const fromArr =
      (eventField(event, '_from') as { name?: string; email?: string }[]) || []
    const fromName =
      fromArr[0]?.name || fromArr[0]?.email || (outbound ? 'You' : actor)
    return {
      ...base,
      type: 'email',
      color: outbound ? undefined : 'success',
      summary: outbound ? (
        <>
          sent an <strong>email</strong>
        </>
      ) : (
        <>
          replied to an <strong>email</strong>
        </>
      ),
      detail: snippet || subject,
      // Same inline reply treatment as the contact detail timeline, plus a
      // "View thread" that opens the full thread in the inbox modal.
      expandedContent: (
        <TimelineEmail
          from={fromName}
          subject={subject}
          body={snippet}
          onViewThread={() => onViewThread(event)}
        />
      ),
    }
  }

  if (model === 'reminder') {
    const title = eventField(event, 'title') as string | undefined
    return {
      ...base,
      type: 'reminder',
      summary: (
        <>
          {getActionText(event.action_type, 'reminder')}
          {title ? (
            <>
              : <strong>{title}</strong>
            </>
          ) : null}
        </>
      ),
    }
  }

  if (model === 'document') {
    const name =
      (eventField(event, 'name') as string | undefined) ??
      event.field_history_objects?.find((f) => f.document_name)?.document_name
    return {
      ...base,
      type: 'document',
      summary:
        event.action_type === 'CREATE'
          ? 'uploaded a document'
          : event.action_type === 'DELETE'
            ? 'removed a document'
            : 'updated a document',
      detail: name || undefined,
    }
  }

  if (event.content_type.app_label === 'engagements' && model === 'capture') {
    return { ...base, type: 'deal', summary: 'submitted a capture form' }
  }

  // Sales-stage change — show the new stage as its colored badge rather than a
  // raw "sales stage id" field name.
  if (
    event.field_name === 'sales_stage' ||
    event.field_name === 'sales_stage_id'
  ) {
    const raw =
      event.value ??
      eventField(event, 'sales_stage') ??
      eventField(event, 'sales_stage_id')
    const stage =
      stages.find((s) => String(s.id) === String(raw)) ??
      stages.find((s) => s.stage?.toLowerCase() === String(raw).toLowerCase())
    return {
      ...base,
      type: 'deal',
      summary: stage ? (
        <>
          moved to{' '}
          <Badge color={stageBadgeColor(stage, stages, customOrderEnabled)}>
            {stage.stage}
          </Badge>
        </>
      ) : (
        'updated the sales stage'
      ),
    }
  }

  // Everything else — reuse the app's activity formatter so the phrasing (and
  // any record names / links) matches the rest of the product, instead of
  // printing raw field names like "created id". Events the formatter can't
  // render are dropped upstream (see eventIsRenderable).
  const iconType: ActivityType =
    model === 'deal'
      ? 'deal'
      : model === 'contact' || model === 'contactdeal'
        ? 'contact'
        : model === 'campaign'
          ? 'campaign'
          : 'account'
  return {
    ...base,
    type: iconType,
    summary: <CellEventFormatterV2 event={event} />,
  }
}

/** Whether the app formatter produces a meaningful phrase for an event — used to
 *  drop audit noise (unhandled ADD/REMOVE through-rows) from the timeline rather
 *  than render an empty row. Note/email/reminder/document/capture/stage events
 *  are handled explicitly before this and are always renderable. */
function eventIsRenderable(event: ActivityV2): boolean {
  if (event.action_type === 'ADD' || event.action_type === 'REMOVE') {
    return ['contactcompany', 'contact', 'doorproxy'].includes(
      event.content_type.model
    )
  }
  return ['CREATE', 'UPDATE', 'DELETE'].includes(event.action_type)
}

/** Map a sales stage to a fb-ui `BadgeColor`: terminals get locked semantic
 *  tones (won→success, lost→error, churn→gray); pipeline stages reuse their
 *  palette key (every STAGE_PALETTE key is also a valid BadgeColor). */
function stageBadgeColor(
  stage: SalesStage,
  allStages: SalesStage[],
  customOrderEnabled: boolean
): BadgeColor {
  const kind = getStageKind(stage)
  if (kind === 'won') return 'success'
  if (kind === 'lost') return 'error'
  if (kind === 'churn') return 'gray'
  return (getStageColorKey(stage, allStages, customOrderEnabled) ??
    'blue') as BadgeColor
}

/** Terminal-outcome glyph shown on the current-stage pill (data-driven per the
 *  StageTracker contract): Closed Won → Confetti, Closed Lost → XCircle,
 *  Churn → Prohibit. Pipeline stages use the tracker's default handshake. */
function terminalStageIcon(stage: SalesStage): React.ReactNode {
  switch (getStageKind(stage)) {
    case 'won':
      return <PiConfetti />
    case 'lost':
      return <PiXCircle />
    case 'churn':
      return <PiProhibit />
    default:
      return undefined
  }
}

/** Compact label for a terminal stage in the StageTracker's visible close-out
 *  group ("Closed Won" → "Won", etc.); other stages keep their full name. */
function terminalShortName(stage: SalesStage): string | undefined {
  switch (getStageKind(stage)) {
    case 'won':
      return 'Won'
    case 'lost':
      return 'Lost'
    case 'churn':
      return 'Churn'
    default:
      return undefined
  }
}

/** Reminder status → fb-ui Badge color, matching the app's existing reminder
 *  status colors (`statusColors` in Reminders/utils): scheduled=purple, sent=
 *  green, failed=red, active=lime, paused=yellow, ended=orange, deleted=gray. */
function reminderStatusBadgeColor(status: ReminderStatus): BadgeColor {
  switch (status) {
    case 'sent':
      return 'success'
    case 'failed':
      return 'error'
    case 'active':
      return 'lime'
    case 'paused':
      return 'yellow'
    case 'ended':
      return 'orange'
    case 'scheduled':
      return 'purple'
    default:
      return 'gray'
  }
}

// ─── Deal Detail Page (fb-ui) ─────────────────────────────────────────────────

/**
 * Deal detail page rebuilt on the @firstbite/ui detail-page blocks — the sibling
 * of {@link ContactDetailPage}, assembled from `EntityDetailRail` +
 * `DetailPanel variant="rail"`. Feature-flagged behind `enable_deal_detail_page_v2`
 * (the wrapper in DealDetailPage.tsx branches to the legacy page when off), so it
 * can merge + release dark while the migration lands piece by piece.
 *
 * Assembled from the fb-ui detail-page blocks: the metadata rail, the work-area
 * header (DetailHeader with freshness stats + Inbox/Actions), the per-deal
 * StageTracker (with gated capture), the note ActivityComposer, and the tabbed
 * activity/events timeline (real event feed → fb-ui ActivityTimeline).
 */
export function DealDetailPageFbui() {
  const { dealId } = useParams<{ dealId: string }>()
  const navigate = useNavigate()
  const api = useApiService()
  const queryClient = useQueryClient()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const { enableQuickAccess, enableContactDetailPage } = useFeatureFlagService()

  const dealIdNum = dealId ? parseInt(dealId, 10) : undefined
  const { data: deal, isLoading, isError, refetch } = api.useGetDeal(dealIdNum)
  const { data: stages } = api.useGetSalesStages()
  useRecordVisit(dealIdNum ? { targetType: 'deal', targetId: dealIdNum } : null)
  const { isPinned, toggle: togglePin } = usePinTarget({
    targetType: 'deal',
    targetId: dealIdNum ?? 0,
  })

  const [showEditLocations, setShowEditLocations] = useState(false)
  const [showEditRelatedCompanies, setShowEditRelatedCompanies] =
    useState(false)
  const [showEditProduct, setShowEditProduct] = useState(false)
  const [showTagModal, setShowTagModal] = useState(false)
  const [showEditDeal, setShowEditDeal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  // Reminder / document creation is reachable from both the header Actions menu
  // and the activity toolbar's contextual "+", so the modal state lives here and
  // both entry points drive it.
  const [showNewReminder, setShowNewReminder] = useState(false)
  const [showUploadDocument, setShowUploadDocument] = useState(false)

  /** Refetch the deal after any mutation (inline edit or a modal save). */
  const handleDealSaved = useCallback(async () => {
    await refetch()
  }, [refetch])

  const invalidateActivity = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['dealActivity', dealIdNum],
    })
  }, [queryClient, dealIdNum])

  const { confirm, dialog: deleteDealDialog } = useConfirmDialog()
  const handleDeleteDeal = useCallback(async () => {
    if (!dealIdNum) return
    const ok = await confirm({
      title: 'Delete this deal?',
      description: 'This permanently removes the deal and cannot be undone.',
      confirmLabel: 'Delete deal',
    })
    if (!ok) return
    try {
      await api.deleteDeal(dealIdNum)
      toast.success('Deal deleted')
      navigate('/deals')
    } catch {
      toast.error('Failed to delete deal')
    }
  }, [api, dealIdNum, navigate, confirm])

  // Stage moves into a gated stage (Closed Won / Closed Lost, etc.) open the
  // required capture form, which commits the move on submit — mirrors the legacy
  // page + Kanban board. Reads are routed to a lagging replica, so we update the
  // cache optimistically and mark stale WITHOUT an immediate refetch (a refetch
  // would race replication and snap the old stage back over our update).
  const { gatedStageMap, isLoading: gatingLoading } = useGatedStageMap()
  const [pendingStageCapture, setPendingStageCapture] = useState<{
    stageId: number
    form: CaptureForm
  } | null>(null)

  const handleStageChange = useCallback(
    async (stageId: number | null) => {
      if (!dealIdNum) return
      if (gatingLoading) return
      if (stageId !== null && gatedStageMap.has(stageId)) {
        setPendingStageCapture({ stageId, form: gatedStageMap.get(stageId)! })
        return
      }
      const queryKey = ['deal', String(dealIdNum)]
      const previous = queryClient.getQueryData<DealDetail>(queryKey)
      if (previous) {
        queryClient.setQueryData<DealDetail>(queryKey, {
          ...previous,
          sales_stage: stageId,
        })
      }
      try {
        await api.editDeal(dealIdNum, { sales_stage: stageId })
        toast.success(stageId === null ? 'Stage cleared' : 'Stage updated')
        void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
        invalidateActivity()
      } catch {
        if (previous) queryClient.setQueryData<DealDetail>(queryKey, previous)
        toast.error('Failed to update stage')
      }
    },
    [
      dealIdNum,
      api,
      queryClient,
      invalidateActivity,
      gatedStageMap,
      gatingLoading,
    ]
  )

  const handleGatedCaptureSuccess = useCallback(() => {
    if (!dealIdNum || !pendingStageCapture) return
    const queryKey = ['deal', String(dealIdNum)]
    const previous = queryClient.getQueryData<DealDetail>(queryKey)
    if (previous) {
      queryClient.setQueryData<DealDetail>(queryKey, {
        ...previous,
        sales_stage: pendingStageCapture.stageId,
      })
    }
    void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
    invalidateActivity()
    setPendingStageCapture(null)
  }, [dealIdNum, pendingStageCapture, queryClient, invalidateActivity])

  /** PATCH a single field of the deal, then refetch + toast. Surfaces the
   *  backend's field message (e.g. a validation error) rather than a generic
   *  failure, matching the contact detail page. */
  const saveField = useCallback(
    async (patch: DealPatch) => {
      if (!dealIdNum) return
      try {
        await api.editDeal(dealIdNum, patch)
        void queryClient.invalidateQueries({
          queryKey: ['deal', String(dealIdNum)],
        })
        toast.success('Deal updated')
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to update deal'))
      }
    },
    [api, dealIdNum, queryClient]
  )

  const handleSaveRelatedCompanies = useCallback(
    async (added: number[], removed: number[]) => {
      if (!dealIdNum) return
      try {
        if (removed.length > 0) {
          await api.manageDealRelatedCompanies(dealIdNum, removed, 'remove')
        }
        if (added.length > 0) {
          await api.manageDealRelatedCompanies(dealIdNum, added, 'add')
        }
        toast.success('Related companies updated')
        await handleDealSaved()
      } catch {
        toast.error('Failed to update related companies')
      }
    },
    [dealIdNum, api, handleDealSaved]
  )

  // ── Note composer ──────────────────────────────────────────────────────────
  // The ActivityComposer's contentEditable surface + toolbar wiring, mirroring
  // the contact detail page (shared exec helpers in utils/richTextComposer).
  const noteMentions = useNoteMentions()
  const noteRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteEmpty, setNoteEmpty] = useState(true)
  const [activeCommands, setActiveCommands] = useState<RichTextCommand[]>([])
  const syncNoteEmpty = () =>
    setNoteEmpty((noteRef.current?.textContent ?? '').trim() === '')
  const refreshActiveCommands = () =>
    setActiveCommands(activeRichTextCommands())

  // Insert-link dialog (an app dialog rather than the native prompt). The editor
  // selection is captured when the command fires and restored on insert, since
  // opening the dialog moves focus off the editor.
  const savedRange = useRef<Range | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const handleCommand = (cmd: RichTextCommand) => {
    if (cmd === 'link') {
      const sel = window.getSelection()
      savedRange.current =
        sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null
      setLinkUrl('')
      setLinkOpen(true)
      return
    }
    if (cmd === 'attachment') {
      fileInputRef.current?.click()
      return
    }
    noteRef.current?.focus()
    RICH_TEXT_EXEC[cmd]?.()
    refreshActiveCommands()
  }

  const insertLink = () => {
    const raw = linkUrl.trim()
    setLinkOpen(false)
    if (!raw) return
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    requestAnimationFrame(() => {
      noteRef.current?.focus()
      const sel = window.getSelection()
      if (savedRange.current && sel) {
        sel.removeAllRanges()
        sel.addRange(savedRange.current)
      }
      if (sel && sel.isCollapsed) {
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`
        )
      } else {
        document.execCommand('createLink', false, url)
      }
      syncNoteEmpty()
    })
  }

  const clearComposer = () => {
    if (noteRef.current) noteRef.current.innerHTML = ''
    setAttachedFiles([])
    setNoteEmpty(true)
    setActiveCommands([])
  }

  const handleLogNote = async () => {
    if (!dealIdNum) return
    const html = noteRef.current?.innerHTML ?? ''
    const text = noteRef.current?.textContent?.trim() ?? ''
    if (!text && attachedFiles.length === 0) return
    setNoteSaving(true)
    try {
      await api.createDealNote(dealIdNum, html, attachedFiles)
      clearComposer()
      void queryClient.invalidateQueries({ queryKey: ['dealNotes', dealIdNum] })
      invalidateActivity()
      toast.success('Note saved')
    } catch {
      toast.error('Failed to save note')
    } finally {
      setNoteSaving(false)
    }
  }

  const allStages = useMemo(
    () =>
      customOrderEnabled
        ? [...(stages ?? [])].sort((a, b) => a.order - b.order)
        : (stages ?? []),
    [stages, customOrderEnabled]
  )

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-surface-sunken">
        <Skeleton className="h-6 w-40" />
      </div>
    )
  }

  if (!deal || isError || !dealIdNum) {
    return <NotFoundContent />
  }

  const dealName =
    deal.name ||
    deal.contact_company?.name ||
    `Deal #${deal.identifier ?? deal.id}`

  // StageTracker is data-driven: map each sales stage to its id/name/color, flag
  // terminals, and give terminals their outcome glyph (won/lost/churn).
  const trackerStages: StageTrackerStage[] = allStages.map((s) => ({
    id: String(s.id),
    name: s.stage,
    shortName: terminalShortName(s),
    color: stageBadgeColor(s, allStages, customOrderEnabled),
    terminal: isTerminalStage(s),
    icon: terminalStageIcon(s),
  }))

  // Minimal ContactCompany shape for the Inbox (EmailsModal) — it only reads id
  // + name to scope the thread list.
  const contactCompanyForModal = deal.contact_company
    ? ({
        id: deal.contact_company.id,
        name: deal.contact_company.name,
      } as unknown as ContactCompany)
    : undefined

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-sunken">
      <div className="min-h-0 flex-1">
        <EntityDetailRail
          rail={
            <DealSidebar
              deal={deal}
              stages={allStages}
              customOrderEnabled={customOrderEnabled}
              saveField={saveField}
              onEditLocations={() => setShowEditLocations(true)}
              onEditRelatedCompanies={() => setShowEditRelatedCompanies(true)}
              onEditProduct={() => setShowEditProduct(true)}
              onEditTags={() => setShowTagModal(true)}
              onEditDeal={() => setShowEditDeal(true)}
              onOpenContact={(contactId) =>
                navigate(`/contacts/detail/${contactId}`)
              }
              contactDetailEnabled={enableContactDetailPage}
            />
          }
        >
          <DetailHeader
            eyebrow="Deal"
            identifier={deal.identifier ?? undefined}
            title={dealName}
            stats={<DealHeaderStats deal={deal} />}
            actions={
              <DealHeaderActions
                deal={deal}
                isPinned={isPinned}
                showPin={enableQuickAccess}
                hasInbox={!!contactCompanyForModal}
                onOpenInbox={() => setShowEmailModal(true)}
                onTogglePin={togglePin}
                onEditDeal={() => setShowEditDeal(true)}
                onEditRelatedCompanies={() => setShowEditRelatedCompanies(true)}
                onNewReminder={() => setShowNewReminder(true)}
                onUploadDocument={() => setShowUploadDocument(true)}
                onDelete={handleDeleteDeal}
              />
            }
          />

          {/* Per-deal pipeline control — back/forward clusters + terminal
              close-out. Moving into a gated stage opens the capture form. */}
          {trackerStages.length > 0 ? (
            <div className="mt-4">
              <StageTracker
                stages={trackerStages}
                currentStageId={
                  deal.sales_stage != null ? String(deal.sales_stage) : null
                }
                onChange={(id) => handleStageChange(Number(id))}
                onClear={() => handleStageChange(null)}
                // Always-visible Won/Lost/Churn group (legacy bar parity).
                terminalStyle="buttons"
              />
            </div>
          ) : null}

          {/* Note composer */}
          <div className="mt-4">
            <ActivityComposer
              types={['note']}
              contentRef={noteRef}
              mentions={noteMentions}
              discardLabel="Cancel"
              disabled={noteSaving}
              onCommand={handleCommand}
              activeCommands={activeCommands}
              onInput={() => {
                syncNoteEmpty()
                refreshActiveCommands()
              }}
              onKeyUp={refreshActiveCommands}
              onMouseUp={refreshActiveCommands}
              // Footer (Save / Cancel) shows once there's text or an attachment.
              onLog={
                noteEmpty && attachedFiles.length === 0
                  ? undefined
                  : handleLogNote
              }
              onDiscard={
                noteEmpty && attachedFiles.length === 0
                  ? undefined
                  : clearComposer
              }
              // Attachment chips sit left of the Save / Cancel actions.
              footerStart={
                attachedFiles.length > 0
                  ? attachedFiles.map((file, i) => (
                      <FilterChip
                        key={`${file.name}-${i}`}
                        value={
                          <span className="inline-flex items-center gap-1">
                            <PiPaperclip size={12} />
                            <span className="max-w-[12rem] truncate">
                              {file.name}
                            </span>
                          </span>
                        }
                        removeLabel={`Remove ${file.name}`}
                        onRemove={() =>
                          setAttachedFiles((prev) =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                      />
                    ))
                  : undefined
              }
            />
            {/* Hidden picker wired to the composer's paperclip command. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              aria-label="Attach files to note"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                if (files.length)
                  setAttachedFiles((prev) => [...prev, ...files])
                e.target.value = ''
              }}
            />
          </div>

          <DealActivitySection
            dealId={deal.id}
            stages={allStages}
            customOrderEnabled={customOrderEnabled}
            onNewReminder={() => setShowNewReminder(true)}
            onUploadDocument={() => setShowUploadDocument(true)}
          />
        </EntityDetailRail>
      </div>

      {/* Primary-company / location scope — an fb-ui Dialog modeled on the
          contact detail page's company edit, using the new contact modal's
          entire-company / select-locations picker flow. */}
      {deal.contact_company && (
        <EditDealLocationsModalFbui
          open={showEditLocations}
          onOpenChange={setShowEditLocations}
          deal={deal}
          onSaved={handleDealSaved}
        />
      )}

      {deal.contact_company && (
        <EditRelatedCompaniesModalFbui
          open={showEditRelatedCompanies}
          onOpenChange={setShowEditRelatedCompanies}
          primaryCompanyId={deal.contact_company.id}
          currentRelatedCompanies={deal.related_companies ?? []}
          onSave={handleSaveRelatedCompanies}
        />
      )}

      <EditDealProductModalFbui
        open={showEditProduct}
        onOpenChange={setShowEditProduct}
        dealId={deal.id}
        currentProductId={deal.product_id}
        currentProductVariantId={deal.product_variant_id}
        onSaved={handleDealSaved}
      />

      <EditDealTagsModal
        open={showTagModal}
        onOpenChange={setShowTagModal}
        dealId={deal.id}
        currentTags={deal.deal_taglist ?? []}
        onSaved={handleDealSaved}
      />

      {/* Full deal editor — reached from the forecast rows' "override here" help
          link (its Overrides section is where revenue/volume overrides live).
          Pending its own fb-ui migration (DEAL-MODAL-FBUI-MIGRATION.md). */}
      <EditDealModal
        show={showEditDeal}
        onClose={() => {
          setShowEditDeal(false)
          void handleDealSaved()
        }}
        dealId={deal.id}
        dealsTableQueryKey={['deal', String(deal.id)]}
      />

      {/* Inbox — the deal's email threads (existing modal, pending its own fb-ui
          migration). */}
      {showEmailModal && contactCompanyForModal ? (
        <EmailsModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          contactCompany={contactCompanyForModal}
          lockedFilters={{
            deal_ids: [deal.id],
            dealLabel: `#${deal.identifier} - ${deal.name || 'Untitled'}`,
          }}
        />
      ) : null}

      {/* New reminder / document upload — reachable from the header Actions menu
          and the activity toolbar "+". On success invalidate the tab queries (and
          the activity feed) so the new record shows without a manual refresh. */}
      {showNewReminder && (
        <ReminderModal
          open
          onClose={() => setShowNewReminder(false)}
          defaultDealIds={[deal.id]}
          onCreated={() => {
            void queryClient.invalidateQueries({
              queryKey: ['dealReminders', deal.id],
            })
            invalidateActivity()
            setShowNewReminder(false)
          }}
        />
      )}
      {showUploadDocument && (
        <UploadDocumentModal
          isOpen={showUploadDocument}
          onClose={() => setShowUploadDocument(false)}
          preSelectedDealId={deal.id}
          onSuccess={() => {
            void queryClient.invalidateQueries({
              queryKey: ['dealDocuments', deal.id],
            })
            invalidateActivity()
            setShowUploadDocument(false)
          }}
        />
      )}

      {deleteDealDialog}

      {/* Required capture when moving into a gated stage (Closed Won/Lost). */}
      {pendingStageCapture && dealIdNum ? (
        <CaptureFormModal
          form={pendingStageCapture.form}
          dealId={dealIdNum}
          targetStageId={pendingStageCapture.stageId}
          isOpen
          onClose={() => setPendingStageCapture(null)}
          onSuccess={handleGatedCaptureSuccess}
        />
      ) : null}

      {/* Insert-link dialog for the note composer's link command. */}
      <OverlayWrapper isOpen={linkOpen} onOpenChange={setLinkOpen}>
        <Backdrop className="max-w-sm">
          <Dialog>
            <DialogHeader>
              <DialogTitle>Insert link</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Input
                // eslint-disable-next-line jsx-a11y/no-autofocus -- expected focus on dialog open
                autoFocus
                type="url"
                placeholder="https://example.com"
                aria-label="Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    insertLink()
                  }
                }}
              />
            </DialogBody>
            <DialogFooter>
              <DialogClose appearance="outline">Cancel</DialogClose>
              <Button onClick={insertLink}>Insert link</Button>
            </DialogFooter>
          </Dialog>
        </Backdrop>
      </OverlayWrapper>
    </div>
  )
}

// ─── Activity section ─────────────────────────────────────────────────────────

/** Small centered empty-state for a timeline tab. */
function ActivityEmpty({
  label,
  icon,
}: {
  label: string
  icon: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-fg-tertiary">
      {icon}
      <span className="text-sm">{label}</span>
    </div>
  )
}

/** Loading placeholder for a timeline/record list — a few rows that echo the
 *  activity row shape (avatar chip + actor line + detail line) so the layout
 *  doesn't jump when real items load. Nicer than a stack of full-width slabs. */
function ActivitySkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-4 py-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2 pt-1">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Shared "Load more" affordance for the paginated activity/record tabs. Renders
 *  nothing when there's no next page. */
function LoadMoreButton({
  hasMore,
  loading,
  onClick,
}: {
  hasMore: boolean
  loading: boolean
  onClick: () => void
}) {
  if (!hasMore) return null
  return (
    <div className="mt-3 flex justify-center">
      <Button
        appearance="outline"
        variant="neutral"
        size="sm"
        disabled={loading}
        onClick={onClick}
      >
        {loading ? 'Loading…' : 'Load more'}
      </Button>
    </div>
  )
}

/** ActivityItem for a real deal note — editable/deletable in place via
 *  {@link DealNoteView}, sourced from the notes endpoint (current records, one
 *  entry per note) rather than the event log. Mirrors the contact detail page. */
function noteToItem(
  note: INote,
  dealId: number,
  onChanged: () => void
): ActivityItem {
  const actor = note.user
    ? `${note.user.first_name} ${note.user.last_name}`.trim() || 'You'
    : 'You'
  return {
    id: `note-${note.id}`,
    timestamp: note.created,
    type: 'note',
    actor,
    summary: (
      <>
        added a <strong>note</strong>
      </>
    ),
    detail: stripHtml(note.content),
    expandedContent: (
      <DealNoteView dealId={dealId} note={note} onChanged={onChanged} />
    ),
  }
}

/** ActivityItem for a reminder record — the title shows in the collapsed
 *  preview; expanding reveals the schedule + body and edit/delete. */
function reminderToItem(
  reminder: ReminderListItem,
  dealId: number,
  onChanged: () => void
): ActivityItem {
  return {
    id: `reminder-${reminder.id}`,
    timestamp: reminder.created,
    type: 'reminder',
    actor: reminder.created_by_name || reminder.user_name || 'First Bite',
    summary: (
      <>
        set a <strong>reminder</strong>
      </>
    ),
    detail: reminder.title,
    expandedContent: (
      <ReminderTimelineView
        dealId={dealId}
        reminder={reminder}
        onChanged={onChanged}
      />
    ),
  }
}

/**
 * The deal's activity feed — real notes (editable) merged with the real event
 * history (emails/reminders/documents/…), in All / Notes / Emails tabs, rendered
 * via the fb-ui ActivityTimeline. Clicking an email opens it (reply happens in
 * that modal). Mirrors the contact detail page.
 */
function DealActivitySection({
  dealId,
  stages,
  customOrderEnabled,
  onNewReminder,
  onUploadDocument,
}: {
  dealId: number
  stages: SalesStage[]
  customOrderEnabled: boolean
  // Reminder / document creation is owned by the page (also on the header Actions
  // menu); the toolbar "+" just invokes these.
  onNewReminder: () => void
  onUploadDocument: () => void
}) {
  // The Recommendations tab is an Action-Center–only surface — gate on the
  // bare `enableActionCenter` flag (not `isHomepageEnabled`, which is broader).
  const { enableActionCenter } = useFeatureFlagService()
  // Controlled so the toolbar's contextual "+" (add) action knows the tab.
  const [tab, setTab] = useState<
    'all' | 'notes' | 'emails' | 'reminders' | 'documents' | 'recommendations'
  >('all')
  // An email event opened as a full thread (inbox modal).
  const [threadEmail, setThreadEmail] = useState<EmailMessage | null>(null)
  const onViewThread = useCallback(
    (ev: ActivityV2) => setThreadEmail(buildEmailFromEvent(ev)),
    []
  )

  // Activity filters — a keyword search, a period, and a team-member filter
  // (like the legacy activity feed). Search/period are client-side; the team
  // filter is server-side for events and client-side for note/reminder records.
  const [activitySearch, setActivitySearch] = useState('')
  const [activityPeriod, setActivityPeriod] = useState<PeriodValue | null>(null)
  const [teamMemberIds, setTeamMemberIds] = useState<string[]>([])
  const { data: teamMembers } = useUsers()
  const teamMemberOptions = useMemo(
    () =>
      (teamMembers ?? []).map((u) => ({
        value: String(u.id),
        label: `${u.first_name} ${u.last_name}`.trim() || u.email,
      })),
    [teamMembers]
  )
  // Recomputed each render (not frozen at mount) so "Last week" etc. stay
  // relative to *now* — the picker re-renders when opened, so the range is
  // current at apply time rather than drifting stale on a long-open page.
  const now = new Date()
  const daysAgo = (d: number) => {
    const x = new Date(now)
    x.setDate(x.getDate() - d)
    return x
  }
  const activityRange = (label: string, days: number): PeriodQuickSelect => ({
    label,
    value: { mode: 'custom', start: daysAgo(days), end: now, label },
  })
  const activityQuickSelects: PeriodQuickSelect[] = [
    activityRange('Last week', 7),
    activityRange('Last month', 30),
    activityRange('Last 3 months', 90),
    activityRange('Last 6 months', 180),
    activityRange('Last year', 365),
  ]

  // The "+" toolbar action is contextual to the tab: reminders → new reminder,
  // documents → upload. Other tabs add via the composer, so no "+" is shown.
  const addLabel =
    tab === 'reminders'
      ? 'New reminder'
      : tab === 'documents'
        ? 'Upload document'
        : null

  return (
    <div className="mt-6">
      <h2 className="sr-only">Activity</h2>
      <TabRoot
        value={tab}
        onValueChange={(v) => setTab(v as typeof tab)}
        variant="minimal"
      >
        <TabList aria-label="Deal activity">
          <TabTrigger value="all">All Activity</TabTrigger>
          <TabTrigger value="notes">Notes</TabTrigger>
          <TabTrigger value="emails">Emails</TabTrigger>
          <TabTrigger value="reminders">Reminders</TabTrigger>
          <TabTrigger value="documents">Documents</TabTrigger>
          {enableActionCenter ? (
            <TabTrigger value="recommendations">Recommendations</TabTrigger>
          ) : null}
        </TabList>

        {/* Search + period filter, plus a contextual "+" add on the record
            tabs (reminders / documents). */}
        <div className="flex items-center gap-2">
          <SearchBar
            className="flex-1"
            size="md"
            shape="rect"
            placeholder="Search activity by keyword"
            aria-label="Search activity by keyword"
            clearable
            value={activitySearch}
            onValueChange={setActivitySearch}
            onClear={() => setActivitySearch('')}
          />
          <PeriodPicker
            label="Period"
            size="md"
            shape="rect"
            showModes={false}
            quickSelects={activityQuickSelects}
            neutralDefault={{
              mode: 'custom',
              start: null,
              end: null,
              label: 'All time',
            }}
            onApply={setActivityPeriod}
            onClear={() => setActivityPeriod(null)}
            // Match the Team member filter's width so the two pills line up.
            className="w-auto min-w-[10rem]"
          />
          <MultiSelectFilter
            label="Team member"
            size="md"
            shape="rect"
            searchable
            searchPlaceholder="Search team"
            options={teamMemberOptions}
            onApply={setTeamMemberIds}
            // FilterTrigger is w-full by default; hug content like the Period
            // filter, but keep a min-width so the notched "Team member" label
            // isn't wider than the (short) "All" value and wraps.
            className="w-auto min-w-[10rem]"
          />
          {addLabel ? (
            <Button
              appearance="outline"
              iconOnly
              size="md"
              className="shrink-0"
              aria-label={addLabel}
              title={addLabel}
              onClick={() =>
                tab === 'reminders' ? onNewReminder() : onUploadDocument()
              }
            >
              <PiPlus />
            </Button>
          ) : null}
        </div>

        <TabContent value="all" className="pt-2">
          <DealActivityTab
            dealId={dealId}
            stages={stages}
            customOrderEnabled={customOrderEnabled}
            onViewThread={onViewThread}
            teamMemberIds={teamMemberIds}
            mode="all"
            search={activitySearch}
            period={activityPeriod}
            emptyLabel="No activity yet"
            emptyIcon={<PiPulse size={28} />}
          />
        </TabContent>
        <TabContent value="notes" className="pt-2">
          <DealActivityTab
            dealId={dealId}
            stages={stages}
            customOrderEnabled={customOrderEnabled}
            onViewThread={onViewThread}
            teamMemberIds={teamMemberIds}
            mode="notes"
            search={activitySearch}
            period={activityPeriod}
            emptyLabel="No notes yet — log one above."
            emptyIcon={<PiNotepad size={28} />}
          />
        </TabContent>
        <TabContent value="emails" className="pt-2">
          <DealActivityTab
            dealId={dealId}
            stages={stages}
            customOrderEnabled={customOrderEnabled}
            onViewThread={onViewThread}
            teamMemberIds={teamMemberIds}
            mode="emails"
            search={activitySearch}
            period={activityPeriod}
            emptyLabel="No emails yet."
            emptyIcon={<PiTray size={28} />}
          />
        </TabContent>
        <TabContent value="reminders" className="pt-2">
          <DealRemindersTab
            dealId={dealId}
            search={activitySearch}
            period={activityPeriod}
            teamMemberIds={teamMemberIds}
          />
        </TabContent>
        <TabContent value="documents" className="pt-2">
          <DealDocumentsTab
            dealId={dealId}
            search={activitySearch}
            period={activityPeriod}
          />
        </TabContent>
        {enableActionCenter ? (
          <TabContent value="recommendations" className="pt-2">
            <DealRecommendationsTab
              dealId={dealId}
              search={activitySearch}
              period={activityPeriod}
              teamMemberIds={teamMemberIds}
            />
          </TabContent>
        ) : null}
      </TabRoot>

      {threadEmail && (
        <EmailsModal
          show={!!threadEmail}
          onHide={() => setThreadEmail(null)}
          emailOnlyMode
          initialEmail={threadEmail}
        />
      )}
    </div>
  )
}

/** One activity tab. Notes come from the notes endpoint (editable); every other
 *  event type from the paginated activity feed. "All" merges both (note events
 *  are dropped from the feed to avoid duplicating the editable notes). */
function DealActivityTab({
  dealId,
  stages,
  customOrderEnabled,
  mode,
  search,
  period,
  teamMemberIds,
  emptyLabel,
  emptyIcon,
  onViewThread,
}: {
  dealId: number
  stages: SalesStage[]
  customOrderEnabled: boolean
  mode: 'all' | 'notes' | 'emails'
  search: string
  period: PeriodValue | null
  teamMemberIds: string[]
  emptyLabel: string
  emptyIcon: React.ReactNode
  onViewThread: (event: ActivityV2) => void
}) {
  const api = useApiService()
  const wantsNotes = mode === 'all' || mode === 'notes'
  const wantsEvents = mode === 'all' || mode === 'emails'
  // Reminders show as editable records in the "All" feed (the dedicated
  // Reminders tab has its own component); the raw reminder events are dropped.
  const wantsReminders = mode === 'all'

  const {
    data: notesData,
    refetch: refetchNotes,
    fetchNextPage: fetchNextNotes,
    hasNextPage: hasNextNotes,
    isFetchingNextPage: isFetchingNextNotes,
    isLoading: notesLoading,
  } = useInfiniteQuery({
    queryKey: ['dealNotes', dealId],
    queryFn: ({ pageParam }) =>
      api.getDealNotes(dealId, { page: pageParam, page_size: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((s, p) => s + (p.results?.length ?? 0), 0)
      return fetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: wantsNotes,
    staleTime: 5 * 60 * 1000,
  })

  const {
    data: remindersData,
    refetch: refetchReminders,
    isLoading: remindersLoading,
  } = useQuery({
    queryKey: ['dealReminders', dealId],
    queryFn: () => api.getReminders({ deal: dealId, page_size: 50 }),
    enabled: wantsReminders,
    staleTime: 5 * 60 * 1000,
  })

  const {
    data: eventsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: eventsLoading,
  } = useInfiniteQuery({
    queryKey: [
      'dealActivity',
      dealId,
      'timeline',
      mode === 'emails' ? 'email' : 'all',
      teamMemberIds,
    ],
    queryFn: ({ pageParam }) =>
      api.getDealActivityList(dealId, {
        page: pageParam,
        page_size: 20,
        ...(mode === 'emails' ? { event_type: 'email' } : {}),
        ...(teamMemberIds.length ? { user: teamMemberIds.map(Number) } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((s, p) => s + p.results.length, 0)
      return fetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: wantsEvents,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const refetchNotesCb = useCallback(() => {
    void refetchNotes()
  }, [refetchNotes])
  const refetchRemindersCb = useCallback(() => {
    void refetchReminders()
  }, [refetchReminders])

  const noteItems = useMemo(
    () =>
      wantsNotes
        ? (notesData?.pages ?? [])
            .flatMap((p) => p.results ?? [])
            .filter(
              (n) =>
                !teamMemberIds.length ||
                (n.user != null && teamMemberIds.includes(String(n.user.id)))
            )
            .map((n) => noteToItem(n, dealId, refetchNotesCb))
        : [],
    [wantsNotes, notesData?.pages, dealId, refetchNotesCb, teamMemberIds]
  )

  const reminderItems = useMemo(
    () =>
      wantsReminders
        ? ((remindersData?.results ?? []) as ReminderListItem[])
            .filter(
              (r) =>
                !teamMemberIds.length ||
                (r.created_by != null &&
                  teamMemberIds.includes(String(r.created_by.id)))
            )
            .map((r) => reminderToItem(r, dealId, refetchRemindersCb))
        : [],
    [
      wantsReminders,
      remindersData?.results,
      dealId,
      refetchRemindersCb,
      teamMemberIds,
    ]
  )

  const eventItems = useMemo(() => {
    if (!wantsEvents) return []
    const events = (eventsData?.pages ?? []).flatMap((p) => p.results)
    return (
      events
        // Drop audit noise the formatter can't phrase (avoids empty rows).
        .filter(eventIsRenderable)
        .filter(
          (e) =>
            !(
              e.content_type?.model === 'emailmessage' &&
              e.action_type === 'UPDATE'
            )
        )
        .filter(
          (e) =>
            e.content_type?.model !== 'document' ||
            (e.field_history_objects && e.field_history_objects.length > 0)
        )
        // In "All", notes + reminders are shown as editable records; drop their
        // raw events from the feed so they don't appear twice.
        .filter(
          (e) =>
            !(
              mode === 'all' &&
              (NOTE_MODELS.has(e.content_type?.model) ||
                e.content_type?.model === 'reminder')
            )
        )
        .map((e) =>
          dealEventToItem(e, stages, customOrderEnabled, onViewThread)
        )
    )
  }, [
    wantsEvents,
    eventsData?.pages,
    mode,
    stages,
    customOrderEnabled,
    onViewThread,
  ])

  const items = useMemo(() => {
    if (mode === 'notes') return noteItems
    if (mode === 'emails') return eventItems
    return [...noteItems, ...reminderItems, ...eventItems].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
  }, [mode, noteItems, reminderItems, eventItems])

  // Keyword + period filter (client-side over the loaded items) — matches the
  // contact detail page's activity filtering.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const start = period?.start ?? null
    const end = period?.end ?? null
    if (!q && !start && !end) return items
    return items.filter((item) => {
      if (q) {
        const hay = `${typeof item.detail === 'string' ? item.detail : ''} ${
          typeof item.actor === 'string' ? item.actor : ''
        }`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (start || end) {
        const t = new Date(item.timestamp as string | number | Date).getTime()
        if (start && t < start.getTime()) return false
        if (end && t > end.getTime()) return false
      }
      return true
    })
  }, [items, search, period])

  const isLoading =
    (wantsNotes && notesLoading) ||
    (wantsEvents && eventsLoading) ||
    (wantsReminders && remindersLoading)

  if (isLoading) {
    return <ActivitySkeleton />
  }

  return (
    <>
      <ActivityTimeline
        items={filtered}
        size="md"
        emptyState={<ActivityEmpty label={emptyLabel} icon={emptyIcon} />}
      />
      {/* Notes tab paginates the notes endpoint; All/Emails paginate the event
          feed. (In All, notes/reminders show their first page merged in; the
          dedicated tabs page through the full records.) */}
      {mode === 'notes' ? (
        <LoadMoreButton
          hasMore={!!hasNextNotes}
          loading={isFetchingNextNotes}
          onClick={() => fetchNextNotes()}
        />
      ) : (
        <LoadMoreButton
          hasMore={wantsEvents && !!hasNextPage}
          loading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        />
      )}
    </>
  )
}

/** Expanded content for a deal note — its rich-text body + attachment links,
 *  with in-place Edit (rich-text) and Delete wired to the deal-notes endpoints.
 *  Mirrors the contact detail page's ContactNoteView. */
function DealNoteView({
  dealId,
  note,
  onChanged,
}: {
  dealId: number
  note: INote
  onChanged: () => void
}) {
  const api = useApiService()
  const mentions = useNoteMentions()
  const { confirm, dialog: confirmDialog } = useConfirmDialog()
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const attachments = (note.attachments ?? []) as {
    id: number
    file: string
    original_filename: string
  }[]
  const att = useNoteEditAttachments(attachments)

  const seedRef = (el: HTMLDivElement | null) => {
    editorRef.current = el
    if (el && el.dataset.seeded !== '1') {
      el.innerHTML = note.content || ''
      el.dataset.seeded = '1'
    }
  }

  const cancelEdit = () => {
    setEditing(false)
    att.reset()
  }

  const saveEdit = async () => {
    setBusy(true)
    try {
      await api.editDealNote(
        dealId,
        note.id,
        editorRef.current?.innerHTML ?? '',
        att.toDelete,
        att.filesToAdd
      )
      setEditing(false)
      att.reset()
      onChanged()
      toast.success('Note updated')
    } catch {
      toast.error('Failed to update note')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!(await confirm({ title: 'Delete this note?' }))) return
    setBusy(true)
    try {
      await api.deleteDealNote(dealId, note.id)
      onChanged()
      toast.success('Note deleted')
    } catch {
      toast.error('Failed to delete note')
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <RichTextEditor
          contentLabel="Edit note"
          contentRef={seedRef}
          contentClassName="min-h-16"
          mentions={mentions}
          onCommand={(cmd) => {
            if (cmd === 'attachment') att.triggerFilePicker()
          }}
        />
        {att.node}
        <div className="flex justify-end gap-2">
          <Button appearance="outline" size="sm" onClick={cancelEdit}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={saveEdit}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div
        className="text-sm text-fg [&_.mention]:font-medium [&_.mention]:text-brand-active [&_a]:text-brand-active [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
        // First-party rich text authored by the team, stored by our backend.
        dangerouslySetInnerHTML={{ __html: note.content || '' }}
      />
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((att) => (
            <a
              key={att.id}
              href={att.file}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-line-subtle bg-surface px-2 py-1 text-xs text-fg-secondary transition-colors hover:text-brand-active"
            >
              <PiPaperclip size={13} />
              <span className="max-w-[12rem] truncate">
                {att.original_filename}
              </span>
            </a>
          ))}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          appearance="outline"
          size="sm"
          leftIcon={<PiPencilSimple aria-hidden="true" />}
          onClick={() => setEditing(true)}
        >
          Edit
        </Button>
        <DeleteIconButton
          label="Delete note"
          disabled={busy}
          onClick={remove}
        />
      </div>
      {confirmDialog}
    </div>
  )
}

/** Expanded content for a reminder in the timeline — schedule + body, with edit
 *  (opens ReminderModal) and delete. Mirrors DealNoteView. */
function ReminderTimelineView({
  dealId,
  reminder,
  onChanged,
}: {
  dealId: number
  reminder: ReminderListItem
  onChanged: () => void
}) {
  const api = useApiService()
  const { confirm, dialog: confirmDialog } = useConfirmDialog()
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const when = reminder.next_run_at ?? reminder.run_at

  const remove = async () => {
    if (!(await confirm({ title: 'Delete this reminder?' }))) return
    setBusy(true)
    try {
      await api.deleteReminder(reminder.id)
      onChanged()
      toast.success('Reminder deleted')
    } catch {
      toast.error('Failed to delete reminder')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-fg-secondary">
        {when
          ? `Scheduled ${dayjs(when).format('MMM D, YYYY · h:mm A')}`
          : 'Not scheduled'}
        {reminder.status ? ` · ${reminder.status}` : ''}
      </p>
      {reminder.content ? (
        <p className="whitespace-pre-wrap text-sm text-fg">
          {reminder.content}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          appearance="outline"
          size="sm"
          leftIcon={<PiPencilSimple aria-hidden="true" />}
          onClick={() => setEditing(true)}
        >
          Edit
        </Button>
        <DeleteIconButton
          label="Delete reminder"
          disabled={busy}
          onClick={remove}
        />
      </div>
      {editing && (
        <ReminderModal
          open
          onClose={() => setEditing(false)}
          defaultDealIds={[dealId]}
          reminderId={reminder.id}
          onCreated={() => {
            onChanged()
            setEditing(false)
          }}
        />
      )}
      {confirmDialog}
    </div>
  )
}

// ─── Reminders + Documents tabs ───────────────────────────────────────────────

/** Keyword (over `text`) + period (over `date`) filter, matching the activity
 *  tabs. Records with no date pass the period check (they're not date-excluded). */
function matchesActivityFilter(
  search: string,
  period: PeriodValue | null,
  text: string,
  date?: string | null
): boolean {
  const q = search.trim().toLowerCase()
  if (q && !text.toLowerCase().includes(q)) return false
  const start = period?.start ?? null
  const end = period?.end ?? null
  if ((start || end) && date) {
    const t = new Date(date).getTime()
    if (start && t < start.getTime()) return false
    if (end && t > end.getTime()) return false
  }
  return true
}

/** Reminders tab — the deal's reminder records, each editable (ReminderModal)
 *  and deletable. Creating a reminder is the toolbar "+" action. */
function DealRemindersTab({
  dealId,
  search,
  period,
  teamMemberIds,
}: {
  dealId: number
  search: string
  period: PeriodValue | null
  teamMemberIds: string[]
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { confirm, dialog: confirmDialog } = useConfirmDialog()
  const [editingId, setEditingId] = useState<number | null>(null)
  // Off by default = active reminders only (matches legacy); on = include ended/
  // inactive too. Server-side via the `is_active` param.
  const [showInactive, setShowInactive] = useState(false)

  // Refresh the paged list + the All-tab query + the inactive-count below (all
  // share the `['dealReminders', dealId]` prefix) after any reminder mutation.
  const refreshReminders = () =>
    void queryClient.invalidateQueries({ queryKey: ['dealReminders', dealId] })

  // `'paged'` key so this infinite query doesn't collide with the All tab's
  // capped `['dealReminders', dealId]` useQuery (same base key, different query
  // type would conflict). Invalidations on the base key prefix-match this too.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['dealReminders', dealId, 'paged', showInactive],
      queryFn: ({ pageParam }) =>
        api.getReminders({
          deal: dealId,
          page: pageParam,
          page_size: 20,
          ...(showInactive ? {} : { is_active: 'true' }),
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce(
          (s, p) => s + (p.results?.length ?? 0),
          0
        )
        return fetched < lastPage.count ? allPages.length + 1 : undefined
      },
      staleTime: 5 * 60 * 1000,
    })

  // How many reminders are inactive (ended/sent/deleted) — shown as "(N)" next
  // to "Show inactive" so it's clear whether flipping it reveals anything.
  const { data: inactiveData } = useQuery({
    queryKey: ['dealReminders', dealId, 'inactive-count'],
    queryFn: () =>
      api.getReminders({ deal: dealId, is_active: 'false', page_size: 1 }),
    staleTime: 5 * 60 * 1000,
  })
  const inactiveCount = inactiveData?.count ?? 0

  const reminders = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((p) => (p.results ?? []) as ReminderListItem[])
        .filter(
          (r) =>
            !teamMemberIds.length ||
            (r.created_by != null &&
              teamMemberIds.includes(String(r.created_by.id)))
        )
        .filter((r) =>
          matchesActivityFilter(
            search,
            period,
            `${r.title} ${r.content ?? ''}`,
            r.next_run_at ?? r.run_at ?? r.created
          )
        ),
    [data?.pages, search, period, teamMemberIds]
  )

  const remove = async (id: number) => {
    if (!(await confirm({ title: 'Delete this reminder?' }))) return
    try {
      await api.deleteReminder(id)
      refreshReminders()
      toast.success('Reminder deleted')
    } catch {
      toast.error('Failed to delete reminder')
    }
  }

  const togglePause = async (reminder: ReminderListItem) => {
    // Pausing keeps the reminder visible (the backend still counts paused as
    // active) with a "Paused" badge + inline Resume — no confirm needed.
    try {
      if (reminder.status === 'paused') {
        await api.unpauseReminder(reminder.id)
        toast.success('Reminder resumed')
      } else {
        await api.pauseReminder(reminder.id)
        toast.success('Reminder paused')
      }
      refreshReminders()
    } catch {
      toast.error('Failed to update reminder')
    }
  }

  return (
    <>
      <div className="mb-2 flex justify-end">
        <Checkbox
          size="sm"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          label={
            inactiveCount > 0
              ? `Show inactive (${inactiveCount})`
              : 'Show inactive'
          }
        />
      </div>
      {isLoading ? (
        <ActivitySkeleton rows={2} />
      ) : reminders.length === 0 ? (
        <ActivityEmpty
          label={showInactive ? 'No reminders.' : 'No active reminders.'}
          icon={<PiBell size={28} />}
        />
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {reminders.map((r) => (
              <ReminderCard
                key={r.id}
                reminder={r}
                onEdit={() => setEditingId(r.id)}
                onDelete={() => remove(r.id)}
                onPauseToggle={() => togglePause(r)}
              />
            ))}
          </div>
          <LoadMoreButton
            hasMore={!!hasNextPage}
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          />
        </>
      )}
      {editingId != null && (
        <ReminderModal
          open
          onClose={() => setEditingId(null)}
          defaultDealIds={[dealId]}
          reminderId={editingId}
          onCreated={() => {
            refreshReminders()
            setEditingId(null)
          }}
        />
      )}
      {confirmDialog}
    </>
  )
}

/** One reminder record — title, schedule/type/status/recipients, body, plus
 *  pause-resume (recurring only), edit and delete. */
function ReminderCard({
  reminder,
  onEdit,
  onDelete,
  onPauseToggle,
}: {
  reminder: ReminderListItem
  onEdit: () => void
  onDelete: () => void
  onPauseToggle: () => void
}) {
  const when = reminder.next_run_at ?? reminder.run_at
  const paused = reminder.status === 'paused'
  // One-off reminders can't be paused; only active/paused recurring ones toggle.
  const canPause =
    reminder.reminder_type !== 'one_off' &&
    (reminder.status === 'active' || reminder.status === 'paused')
  const recipients = reminder.recipients_names ?? []
  const status =
    reminder.status.charAt(0).toUpperCase() + reminder.status.slice(1)

  return (
    <div className="rounded-lg border border-line-subtle bg-surface p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-fg">
            {reminder.title}
          </p>
          {/* Schedule as plain text; frequency + status as chips (status mapped
              to the app's existing reminder-status colors). */}
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-fg-secondary">
            <span>
              {when
                ? dayjs(when).format('MMM D, YYYY · h:mm A')
                : 'Not scheduled'}
            </span>
            <Badge color="gray" size="sm">
              {reminderTypeFormat(reminder)}
            </Badge>
            <Badge color={reminderStatusBadgeColor(reminder.status)} size="sm">
              {status}
            </Badge>
          </div>
          {recipients.length > 0 ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-fg-secondary">
              <PiUser size={12} aria-hidden="true" />
              {recipients.slice(0, 3).join(', ')}
              {recipients.length > 3 ? ` +${recipients.length - 3}` : ''}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-1">
          {canPause ? (
            <Button
              appearance="outline"
              variant="neutral"
              iconOnly
              size="sm"
              aria-label={paused ? 'Resume reminder' : 'Pause reminder'}
              title={paused ? 'Resume' : 'Pause'}
              onClick={onPauseToggle}
            >
              {paused ? <PiPlay /> : <PiPause />}
            </Button>
          ) : null}
          <Button
            appearance="outline"
            variant="neutral"
            iconOnly
            size="sm"
            aria-label="Edit reminder"
            onClick={onEdit}
          >
            <PiPencilSimple />
          </Button>
          <Button
            appearance="outline"
            variant="neutral"
            iconOnly
            size="sm"
            aria-label="Delete reminder"
            onClick={onDelete}
            className="hover:!bg-error-bg-subtle hover:!text-error-strong"
          >
            <PiTrash />
          </Button>
        </div>
      </div>
      {reminder.content ? (
        <p className="mt-1.5 whitespace-pre-wrap text-sm text-fg-secondary">
          {reminder.content}
        </p>
      ) : null}
    </div>
  )
}

/** Documents tab — the deal's document records, each viewable/downloadable. */
function DealDocumentsTab({
  dealId,
  search,
  period,
}: {
  dealId: number
  search: string
  period: PeriodValue | null
}) {
  const queryClient = useQueryClient()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['dealDocuments', dealId],
      queryFn: ({ pageParam }) =>
        getDocuments({
          deal: [dealId],
          ordering: '-created',
          page: pageParam,
          page_size: 20,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const fetched = allPages.reduce((s, p) => s + p.results.length, 0)
        return fetched < lastPage.count ? allPages.length + 1 : undefined
      },
      staleTime: 5 * 60 * 1000,
    })

  const onDeleted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dealDocuments', dealId] })
    void queryClient.invalidateQueries({ queryKey: ['dealActivity', dealId] })
  }, [queryClient, dealId])

  const docs = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((p) => p.results as Document[])
        .filter((d) =>
          matchesActivityFilter(search, period, d.name, d.created)
        ),
    [data?.pages, search, period]
  )

  if (isLoading) return <ActivitySkeleton rows={2} />
  if (docs.length === 0)
    return (
      <ActivityEmpty
        label="No documents yet."
        icon={<PiFileText size={28} />}
      />
    )
  return (
    <>
      <div className="flex flex-col gap-2">
        {docs.map((d) => (
          <DocumentRow key={d.id} doc={d} onDeleted={onDeleted} />
        ))}
      </div>
      <LoadMoreButton
        hasMore={!!hasNextPage}
        loading={isFetchingNextPage}
        onClick={() => fetchNextPage()}
      />
    </>
  )
}

/** One document record — name, metadata (type · size · tags), a download action
 *  (real blob fetch, so cross-origin S3 files download instead of opening a tab),
 *  and delete. */
function DocumentRow({
  doc,
  onDeleted,
}: {
  doc: Document
  onDeleted: () => void
}) {
  const deleteDoc = useDeleteDocument()
  const { confirm, dialog: confirmDialog } = useConfirmDialog()
  const [downloading, setDownloading] = useState(false)

  const remove = async () => {
    if (!(await confirm({ title: 'Delete this document?' }))) return
    try {
      await deleteDoc.mutateAsync({ id: doc.id })
      toast.success('Document deleted')
      onDeleted()
    } catch {
      toast.error('Failed to delete document')
    }
  }

  const download = async () => {
    if (!doc.file) return
    setDownloading(true)
    try {
      const res = await fetch(doc.file)
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = doc.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download file')
    } finally {
      setDownloading(false)
    }
  }

  const meta = [
    doc.file_extension ? doc.file_extension.toUpperCase() : null,
    doc.size != null ? formatBytes(doc.size) : null,
    doc.tags?.length ? doc.tags.map((t) => t.tag).join(', ') : null,
  ].filter(Boolean)

  const FileIcon = documentIcon(doc.file_extension)

  return (
    <div className="flex items-center gap-3 rounded-lg border border-line-subtle bg-surface p-3">
      <FileIcon className="shrink-0 text-fg-tertiary" size={20} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{doc.name}</p>
        <p className="truncate text-xs text-fg-secondary">
          {[
            doc.created ? dayjs(doc.created).format('MMM D, YYYY') : null,
            ...meta,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      {doc.file ? (
        <Button
          appearance="outline"
          size="sm"
          disabled={downloading}
          onClick={download}
        >
          <PiDownloadSimple aria-hidden="true" />
          {downloading ? 'Downloading…' : 'Download'}
        </Button>
      ) : null}
      <DeleteIconButton
        label="Delete document"
        disabled={deleteDoc.isPending}
        onClick={remove}
      />
      {confirmDialog}
    </div>
  )
}

// ─── Recommendations tab (Action Center only) ─────────────────────────────────

/**
 * Human, noun-phrase labels for the applied-recommendation action types so they
 * read after "recommended …" (mirrors recommendations.models.ActionType on the
 * server). Unknown types fall back to the humanized raw slug.
 */
const RECOMMENDATION_ACTION_LABELS: Record<string, string> = {
  update_stage: 'a stage change',
  update_close_date: 'a close-date change',
  create_reminder: 'a new reminder',
  reschedule_reminder: 'rescheduling a reminder',
  delete_reminder: 'deleting a reminder',
  add_note: 'a note',
  send_email: 'an email',
  create_contact: 'a new contact',
  update_contact: 'a contact update',
}

/** Map an applied-recommendation history entry to an fb-ui {@link ActivityItem}:
 *  the Sparkle "ai" glyph, the action as the sentence, and any field diff as the
 *  secondary detail. */
function recommendationToItem(item: RecommendationHistoryItem): ActivityItem {
  const label =
    RECOMMENDATION_ACTION_LABELS[item.action_type] ??
    item.action_type.replace(/_/g, ' ')
  const changes = item.applied_changes ?? []
  return {
    id: `rec-${item.id}`,
    timestamp: item.clicked_at,
    type: 'ai',
    actor: 'First Bite AI',
    summary: (
      <>
        recommended <strong>{label}</strong>
      </>
    ),
    detail:
      changes.length > 0 ? (
        <div className="space-y-0.5">
          {changes.map((c) => (
            <div key={c.field} className="text-sm text-fg-secondary">
              {c.label}: <span className="text-fg">{c.from ?? '—'}</span>
              {' → '}
              <span className="text-fg">{c.to ?? '—'}</span>
            </div>
          ))}
        </div>
      ) : undefined,
  }
}

/** The Recommendations tab — applied AI recommendation actions for the deal,
 *  rendered in the shared activity timeline. Action-Center–only (gated by the
 *  caller). Date + team-member filters run server-side via the history hook; the
 *  keyword search filters the loaded items client-side (like the other tabs). */
function DealRecommendationsTab({
  dealId,
  search,
  period,
  teamMemberIds,
}: {
  dealId: number
  search: string
  period: PeriodValue | null
  teamMemberIds: string[]
}) {
  const dateRange = period?.start
    ? { from: period.start, to: period.end ?? undefined }
    : undefined
  const userIds = useMemo(() => teamMemberIds.map(Number), [teamMemberIds])
  const {
    items: rawItems,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useDealRecommendationHistory(dealId, { dateRange, userIds })

  const items = useMemo(
    () =>
      rawItems
        .filter((r) =>
          matchesActivityFilter(
            search,
            null,
            `${r.recommendation_text ?? ''} ${r.action_text ?? ''} ${(
              r.applied_changes ?? []
            )
              .map((c) => `${c.label} ${c.from ?? ''} ${c.to ?? ''}`)
              .join(' ')}`,
            r.clicked_at
          )
        )
        .map(recommendationToItem),
    [rawItems, search]
  )

  if (isLoading) {
    return <ActivitySkeleton />
  }
  if (error) {
    return (
      <ActivityEmpty
        label="Couldn't load recommendations."
        icon={<PiSparkle size={28} />}
      />
    )
  }

  return (
    <>
      <ActivityTimeline
        items={items}
        size="md"
        emptyState={
          <ActivityEmpty
            label="No recommendations applied yet."
            icon={<PiSparkle size={28} />}
          />
        }
      />
      {hasNextPage ? (
        <div className="mt-3 flex justify-center">
          <Button
            appearance="outline"
            variant="neutral"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </>
  )
}

// ─── Header slots ─────────────────────────────────────────────────────────────

/** Freshness/recency stats — days since last CRM update, last sent, and last
 *  received, each colored by staleness (green → amber → red via the app-wide
 *  `getDaysSinceColorClass` thresholds), matching the legacy header. */
function DealHeaderStats({ deal }: { deal: DealDetail }) {
  const daysSinceModified = deal.modified
    ? Math.floor(dayjs().diff(dayjs(deal.modified), 'day'))
    : null
  return (
    <>
      {daysSinceModified !== null ? (
        <StatusStat
          layout="inline"
          iconChip
          size="md"
          color={freshnessColor(daysSinceModified)}
          icon={<PiHourglass />}
        >
          {daysSinceModified === 0
            ? 'Updated today'
            : `${daysSinceModified}d since update`}
        </StatusStat>
      ) : null}
      {deal.days_since_sent != null ? (
        <StatusStat
          layout="inline"
          iconChip
          size="md"
          color={freshnessColor(deal.days_since_sent)}
          icon={<PiPaperPlaneRight />}
        >
          {deal.days_since_sent === 0
            ? 'Sent today'
            : `${deal.days_since_sent}d since sent`}
        </StatusStat>
      ) : null}
      {deal.days_since_received != null ? (
        <StatusStat
          layout="inline"
          iconChip
          size="md"
          color={freshnessColor(deal.days_since_received)}
          icon={<PiTray />}
        >
          {deal.days_since_received === 0
            ? 'Received today'
            : `${deal.days_since_received}d since received`}
        </StatusStat>
      ) : null}
    </>
  )
}

/** Header controls — an Inbox button (deal email threads) + an Actions menu. */
function DealHeaderActions({
  deal,
  isPinned,
  showPin,
  hasInbox,
  onOpenInbox,
  onTogglePin,
  onEditDeal,
  onEditRelatedCompanies,
  onNewReminder,
  onUploadDocument,
  onDelete,
}: {
  deal: DealDetail
  isPinned: boolean
  showPin: boolean
  hasInbox: boolean
  onOpenInbox: () => void
  onTogglePin: () => void
  onEditDeal: () => void
  onEditRelatedCompanies: () => void
  onNewReminder: () => void
  onUploadDocument: () => void
  onDelete: () => void
}) {
  // Inbox is a lone secondary action (no channel-icon cluster like the contact
  // header), so a bare icon reads as too subtle next to the primary green
  // Actions. A neutral outline button gives it proper secondary weight.
  return (
    <>
      {hasInbox ? (
        <Button variant="ghost" onClick={onOpenInbox}>
          <PiTray />
          Inbox
          {deal.assigned_thread_count ? (
            // White chip (no border) so the count reads as a distinct pill on the
            // ghost button's tinted hover.
            <span className="ml-1 rounded-full bg-surface px-1.5 py-0.5 text-xs font-medium tabular-nums text-fg-secondary">
              {deal.assigned_thread_count}
            </span>
          ) : null}
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Arbitrary `ml-[3px]` (not `ml-1`) so Bootstrap's spacing utilities
              can't shadow the gap from the Inbox button. */}
          <Button size="md" className="ml-[3px]">
            Actions
            <PiCaretDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem icon={<PiPencilSimple />} onSelect={onEditDeal}>
            Edit deal
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<PiBuildings />}
            onSelect={onEditRelatedCompanies}
          >
            Edit related companies
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem icon={<PiBell />} onSelect={onNewReminder}>
            Schedule reminder
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<PiUploadSimple />}
            onSelect={onUploadDocument}
          >
            Upload document
          </DropdownMenuItem>
          {showPin ? (
            <DropdownMenuItem
              icon={isPinned ? <PiPushPinSlash /> : <PiPushPin />}
              onSelect={() => {
                onTogglePin()
                toast.success(
                  isPinned ? 'Unpinned from homepage' : 'Pinned to homepage'
                )
              }}
            >
              {isPinned ? 'Unpin from homepage' : 'Pin to homepage'}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive icon={<PiTrash />} onSelect={onDelete}>
            Delete deal
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

// ─── Sidebar rail ─────────────────────────────────────────────────────────────

/** A forecast row's label with a help popover explaining that the figure is
 *  predicted, and a "here" link that opens the full deal editor (where the
 *  revenue/volume overrides live). Copy adapts when an override is already set. */
function ForecastLabel({
  label,
  overridden,
  onEditDeal,
}: {
  label: string
  overridden: boolean
  onEditDeal: () => void
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <HelpPopover
        title={overridden ? 'Manual override' : 'Forecasted value'}
        triggerSize="sm"
        side="right"
      >
        {overridden
          ? 'This value is a manual override. To change or clear it, edit the deal '
          : 'This value is forecasted automatically. To override it, edit the deal '}
        <button
          type="button"
          onClick={onEditDeal}
          className="font-medium text-brand-active hover:text-basil-800"
        >
          here
        </button>
        .
      </HelpPopover>
    </span>
  )
}

/** A deal contact's trailing 3-dot control — opens a quick-glance card (the
 *  homepage EntityPreviewCard style) with the contact's email (mailto + copy)
 *  and a "View contact" link to the full detail page. (Phone isn't in the deal's
 *  contact payload yet — it'd need a serializer field to surface here.) */
function ContactRowActions({
  contact,
  contactDetailEnabled,
  onView,
}: {
  contact: NonNullable<DealDetail['contacts']>[number]
  contactDetailEnabled: boolean
  onView: () => void
}) {
  const fullName =
    `${contact.first_name} ${contact.last_name}`.trim() || 'Contact'
  const subtitle =
    contact.title && contact.company_name
      ? `${contact.title} · ${contact.company_name}`
      : contact.title || contact.company_name || undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${fullName} details`}
          // Don't let opening the card also trigger the row's detail nav.
          onClick={(e) => e.stopPropagation()}
          className="text-fg-tertiary transition-colors hover:text-fg-secondary"
        >
          <Ellipsis size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto border-0 bg-transparent p-0 shadow-none"
      >
        <EntityPreviewCard
          type="contact"
          // The row already shows the contact glyph; drop it in the popover to
          // avoid the redundancy and reclaim card space.
          hideGlyph
          name={fullName}
          subtitle={subtitle}
          actions={
            contactDetailEnabled ? (
              <Button
                appearance="outline"
                size="sm"
                className="w-full"
                onClick={onView}
              >
                View contact
              </Button>
            ) : undefined
          }
        >
          {contact.email ? (
            <div className="flex items-center gap-2 text-sm text-fg-secondary">
              <PiEnvelope className="shrink-0 text-fg-tertiary" size={16} />
              <a
                href={`mailto:${contact.email}`}
                className="min-w-0 flex-1 truncate transition-colors hover:text-brand-active"
              >
                {contact.email}
              </a>
              <button
                type="button"
                aria-label="Copy email"
                onClick={() =>
                  void navigator.clipboard?.writeText(contact.email!)
                }
                className="shrink-0 text-fg-tertiary transition-colors hover:text-brand-active"
              >
                <PiCopy size={14} />
              </button>
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">No email on file</p>
          )}
        </EntityPreviewCard>
      </PopoverContent>
    </Popover>
  )
}

/** The left DetailPanel — Summary, Primary Company, Related Companies, Products,
 *  Distributors, GPOs, Contacts, and Tags sections. Editable summary fields
 *  PATCH via `saveField`; relational sections open the existing edit modals via
 *  a hover-revealed header pencil. */
function DealSidebar({
  deal,
  stages,
  customOrderEnabled,
  saveField,
  onEditLocations,
  onEditRelatedCompanies,
  onEditProduct,
  onEditTags,
  onEditDeal,
  onOpenContact,
  contactDetailEnabled,
}: {
  deal: DealDetail
  stages: SalesStage[]
  customOrderEnabled: boolean
  saveField: (patch: DealPatch) => void
  onEditLocations: () => void
  onEditRelatedCompanies: () => void
  onEditProduct: () => void
  onEditTags: () => void
  onEditDeal: () => void
  onOpenContact: (contactId: number) => void
  contactDetailEnabled: boolean
}) {
  const api = useApiService()
  const { preferences } = usePreferences()
  const reportingUnit = preferences?.reporting_unit || 'lbs'

  // Account-owner options — active, non-Limited users only (brokers/Limited
  // users can't own deals, per FIR-8017), matching the EditDealModal picker.
  const { data: userOptions } = useUserOptions({
    onlyActive: true,
    excludeLimited: true,
  })
  const ownerOptions: EditableSelectOption[] = useMemo(
    () =>
      (userOptions ?? []).map((u) => ({
        value: String(u.value),
        label: u.label,
      })),
    [userOptions]
  )

  // Resolve the deal's origin campaign (a provenance id on the deal) to its name
  // for the Summary row. Only fetched when the deal actually has one; falls back
  // to "#id" until the name resolves.
  const { data: campaignOptions } = api.useGetCampaignsOptions({
    disabled: !deal.origin_campaign,
  })
  const originCampaignName = deal.origin_campaign
    ? campaignOptions?.find((o) => o.value === deal.origin_campaign)?.label
    : undefined

  const company = deal.contact_company
  const relatedCompanies = deal.related_companies ?? []
  const product = deal.product_detail
  const contacts = deal.contacts ?? []
  const distributors = company?.distributors ?? []
  const gpos = company?.gpos ?? []
  const tags = deal.deal_taglist ?? []

  const stageEntry = stages.find((s) => s.id === deal.sales_stage)
  const stageColor = stageEntry
    ? getStageColorHex(stageEntry, stages, customOrderEnabled)
    : null

  const volumeLabel = deal.is_not_recurring
    ? deal.monthly_volume_override != null
      ? 'Est. Volume Override'
      : 'Est. Volume'
    : deal.monthly_volume_override != null
      ? '1yr Volume Override'
      : '1yr Volume Forecast'
  const volumeDisplay =
    deal.volume_display != null
      ? `${deal.volume_display.toLocaleString()} ${reportingUnit.toUpperCase()}`
      : '—'

  const revenueLabel = deal.is_not_recurring
    ? 'Total Deal Value'
    : deal.monthly_revenue_override != null
      ? '1yr Revenue Override'
      : '1yr Revenue Forecast'

  const doorsCount = deal.doors_count ?? 0
  const totalCompanyDoors = company?.total_doors ?? 0
  const coversAllLocations =
    deal.apply_to === 'entire_company' || doorsCount >= totalCompanyDoors
  const coverageText = coversAllLocations
    ? totalCompanyDoors === 1
      ? '1 location'
      : `All ${locationsLabel(totalCompanyDoors)}`
    : `${doorsCount.toLocaleString()} of ${locationsLabel(totalCompanyDoors)}`
  // The deal's chosen variant — or, when none is chosen but the product has
  // exactly one, that sole variant (it's unambiguously the product's variant, so
  // its name reads better than a bare "1 variant" count).
  const selectedVariant = product
    ? ((deal.product_variant_id
        ? product.variants.find((v) => v.id === deal.product_variant_id)
        : undefined) ??
      (product.variants.length === 1 ? product.variants[0] : undefined))
    : undefined

  return (
    <DetailPanel variant="rail">
      {/* Summary — headline figures (Deal Value / Est. Close) fold in as rows,
          per the detail-panel convention (no separate metric header). */}
      <DetailSection title="Summary">
        <InfoList>
          <EditableInfoRow
            label="Name"
            value={deal.name}
            placeholder="Add deal name"
            onCommit={(v) => saveField({ name: v })}
          />
          <InfoRow label="ID" numeric>
            {deal.identifier ? `#${deal.identifier}` : '—'}
          </InfoRow>
          <InfoRow label="Stage">
            {stageEntry && stageColor ? (
              <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{
                  backgroundColor: stageColor.bg,
                  color: stageColor.text,
                }}
              >
                {stageEntry.stage}
              </span>
            ) : (
              '—'
            )}
          </InfoRow>
          <EditableInfoRow
            label="Account Owner"
            // Undefined (not '') when unassigned, so the placeholder shows
            // instead of a blank value (`??` only falls through on nullish).
            value={
              deal.account_owner != null
                ? String(deal.account_owner)
                : undefined
            }
            displayValue={deal.account_owner_name || undefined}
            options={ownerOptions}
            placeholder="Add owner"
            onCommit={(v) =>
              saveField({ account_owner: v ? Number(v) : undefined })
            }
          />
          <InfoRow
            label={
              <ForecastLabel
                label={revenueLabel}
                overridden={deal.monthly_revenue_override != null}
                onEditDeal={onEditDeal}
              />
            }
            numeric
          >
            {usdFormatterOrFallback(deal.revenue_display)}
          </InfoRow>
          <InfoRow
            label={
              <ForecastLabel
                label={volumeLabel}
                overridden={deal.monthly_volume_override != null}
                onEditDeal={onEditDeal}
              />
            }
            numeric
          >
            {volumeDisplay}
          </InfoRow>
          {deal.is_not_recurring && deal.monthly_revenue_override != null ? (
            <InfoRow label="Est. Price Override" numeric>
              {usdFormatterOrFallback(deal.monthly_revenue_override)}
            </InfoRow>
          ) : null}
          <EditableInfoRow
            label="Est. Close"
            type="date"
            numeric
            value={
              deal.close_date_override
                ? dayjs(deal.close_date_override).format('YYYY-MM-DD')
                : ''
            }
            displayValue={
              deal.close_date_override
                ? fmtDate(deal.close_date_override)
                : undefined
            }
            placeholder="Set close date"
            onCommit={(v) => saveField({ close_date_override: v || undefined })}
          />
          {deal.origin_campaign ? (
            <InfoRow label="Origin Campaign">
              <Link
                to={`/campaigns/${deal.origin_campaign}`}
                className="font-medium text-fg transition-colors hover:text-brand-active"
              >
                {originCampaignName ?? `#${deal.origin_campaign}`}
              </Link>
            </InfoRow>
          ) : null}
          <InfoRow label="Date Added" numeric>
            {fmtDate(deal.created)}
          </InfoRow>
        </InfoList>
      </DetailSection>

      {/* Primary Company — a deal's company isn't reassignable; the only edit is
          the location scope, reached by clicking the locations row below (so no
          separate header pencil is needed). */}
      {company ? (
        <DetailSection title="Primary Company">
          <EntitySummaryCard
            bordered={false}
            type="company"
            name={company.name}
            href={company.url || undefined}
            subtitle={company.sub_category?.name ?? 'Company'}
          >
            {/* The locations row is the single, actionable summary of coverage:
                click it to open the locations viewer/editor (the dense address
                preview it replaced was duplicative and not interactive). Only
                clickable when there's more than one location to scope. */}
            {totalCompanyDoors > 0 ? (
              <EntitySummaryRow icon={<PiMapTrifold />}>
                {totalCompanyDoors > 1 ? (
                  <button
                    type="button"
                    onClick={onEditLocations}
                    aria-label="View and edit locations"
                    className="group/loc -mx-1 inline-flex items-center gap-1 rounded px-1 text-left transition-colors hover:text-brand-active"
                  >
                    {coverageText}
                    <PiCaretRight
                      size={12}
                      className="text-fg-tertiary transition-colors group-hover/loc:text-brand-active"
                    />
                  </button>
                ) : (
                  coverageText
                )}
              </EntitySummaryRow>
            ) : null}
          </EntitySummaryCard>
        </DetailSection>
      ) : null}

      {/* Related Companies — the header edit pencil only when there's something
          to edit; when empty, the in-body "Add related company" pill covers it. */}
      <DetailSection
        title="Related Companies"
        count={relatedCompanies.length || undefined}
        revealActions={relatedCompanies.length > 0}
        headerRight={
          relatedCompanies.length > 0 ? (
            <Button
              variant="neutral"
              iconOnly
              size="sm"
              aria-label="Edit related companies"
              onClick={onEditRelatedCompanies}
            >
              <PiPencilSimple />
            </Button>
          ) : undefined
        }
      >
        {relatedCompanies.length > 0 ? (
          <div className="flex flex-col gap-2">
            {relatedCompanies.map((rc) => (
              <EntitySummaryCard
                key={rc.id}
                bordered={false}
                type="company"
                name={rc.name}
                href={rc.url || undefined}
                subtitle={rc.sub_category?.name ?? 'Company'}
              />
            ))}
          </div>
        ) : (
          <AddPill className="self-start" onClick={onEditRelatedCompanies}>
            Add related company
          </AddPill>
        )}
      </DetailSection>

      {/* Products */}
      {product ? (
        <DetailSection
          title="Products"
          revealActions
          headerRight={
            <Button
              variant="neutral"
              iconOnly
              size="sm"
              aria-label="Edit product"
              onClick={onEditProduct}
            >
              <PiPencilSimple />
            </Button>
          }
        >
          <EntitySummaryCard
            bordered={false}
            type="product"
            // Plain text, not a link — a deal's product has no dedicated page,
            // and linking to the whole products table reads as misleading. The
            // header pencil is the view/edit entry.
            name={product.name}
            // The format when known (e.g. "Pouches"); omitted otherwise — a
            // generic "Product" line is redundant in a Products section.
            subtitle={product.product_format || undefined}
          >
            {/* Variant rows only when there's something to show — a selected
                variant, or a count of choosable variants. A product with no
                variants shows just its name/format (no dead "No variants" row).
                The product itself is always editable via the header pencil. */}
            {selectedVariant ? (
              <>
                <EntitySummaryRow icon={<PiPackage />}>
                  {selectedVariant.name}
                </EntitySummaryRow>
                {selectedVariant.sku ? (
                  <EntitySummaryRow
                    icon={<PiBarcode />}
                    className="tabular-nums"
                  >
                    {selectedVariant.sku}
                  </EntitySummaryRow>
                ) : null}
              </>
            ) : product.variants.length > 0 ? (
              <EntitySummaryRow icon={<PiPackage />}>
                {product.variants.length}{' '}
                {product.variants.length === 1 ? 'variant' : 'variants'}
              </EntitySummaryRow>
            ) : null}
          </EntitySummaryCard>
        </DetailSection>
      ) : null}

      {/* Distributors */}
      {distributors.length > 0 ? (
        <DetailSection title="Distributors" count={distributors.length}>
          <div className="flex flex-wrap gap-1.5">
            {distributors.map((d) => (
              <Badge key={d.id} color="gray">
                {d.name}
              </Badge>
            ))}
          </div>
        </DetailSection>
      ) : null}

      {/* GPOs */}
      {gpos.length > 0 ? (
        <DetailSection title="GPOs" count={gpos.length}>
          <div className="flex flex-wrap gap-1.5">
            {gpos.map((g) => (
              <Badge key={g.id} color="gray">
                {g.name}
              </Badge>
            ))}
          </div>
        </DetailSection>
      ) : null}

      {/* Contacts */}
      {contacts.length > 0 ? (
        <DetailSection title="Contacts" count={contacts.length}>
          {/* ~4 rows before scroll, to keep the rail compact. */}
          <div className="fbui-scroll flex max-h-48 flex-col overflow-y-auto">
            {contacts.map((c) => (
              <EntityRow
                key={c.id}
                type="contact"
                // `sm` glyph (size-8) to match the Products/Company summary-card
                // glyphs; the row's default `md` reads oversized beside them.
                size="sm"
                title={`${c.first_name} ${c.last_name}`.trim() || 'Contact'}
                subtitle={
                  c.title && c.company_name
                    ? `${c.title} · ${c.company_name}`
                    : c.title || c.company_name || undefined
                }
                // A 3-dot control opens a quick-glance card (email + view in
                // full) — replaces the bare company icon.
                meta={
                  <ContactRowActions
                    contact={c}
                    contactDetailEnabled={contactDetailEnabled}
                    onView={() => onOpenContact(c.id)}
                  />
                }
                // The row opens the contact's detail page (when that page is
                // enabled — same gating as the contacts table name link).
                onClick={
                  contactDetailEnabled ? () => onOpenContact(c.id) : undefined
                }
              />
            ))}
          </div>
        </DetailSection>
      ) : (
        <DetailSection title="Contacts">
          {/* text-xs to match the rail's other section-body rows
              (EntitySummaryRow / EntityRow subtitles), not text-sm. */}
          <div className="flex items-center gap-2 text-xs text-fg-tertiary">
            <PiAddressBook />
            No contacts assigned.
          </div>
        </DetailSection>
      )}

      {/* Tags — mirrors the contact rail's Labels section: a hover-revealed gear
          links to the Deal Tags settings tab (create/rename/color), while the
          in-body Add pill assigns tags via the fb-ui picker dialog. */}
      <DetailSection
        title="Tags"
        count={tags.length || undefined}
        revealActions
        headerRight={
          <Button
            asChild
            variant="neutral"
            iconOnly
            size="sm"
            aria-label="Manage deal tags in settings"
            title="Manage deal tags in settings"
          >
            <Link to="/settings/data/deal-tags">
              <PiGear />
            </Link>
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <TagPill key={tag.id} label={tag.tag} color={tag.color} />
              ))}
            </div>
          ) : null}
          <AddPill className="self-start" onClick={onEditTags}>
            {tags.length ? 'Edit tags' : 'Add tag'}
          </AddPill>
        </div>
      </DetailSection>
    </DetailPanel>
  )
}

// ─── Tags modal (fb-ui) ───────────────────────────────────────────────────────

type DealTag = { id: number; tag: string; color: string | null }

/** fb-ui Dialog for assigning a deal's tags — select existing tags or type a new
 *  one, then diff against the current set and add/remove via the deal-tags bulk
 *  endpoint. Mirrors the contact rail's Labels dialog. */
function EditDealTagsModal({
  open,
  onOpenChange,
  dealId,
  currentTags,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  dealId: number
  currentTags: DealTag[]
  onSaved: () => void
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-md">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Tags</DialogTitle>
          </DialogHeader>
          {/* Keyed on `open` so the draft reseeds from the current tags each
              time the dialog opens (no effect needed). */}
          <DealTagsModalBody
            key={open ? 'open' : 'closed'}
            dealId={dealId}
            currentTags={currentTags}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

function DealTagsModalBody({
  dealId,
  currentTags,
  onOpenChange,
  onSaved,
}: {
  dealId: number
  currentTags: DealTag[]
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const { data: allTags } = api.useGetTags('deals')

  const options: MultiComboboxOption[] = useMemo(
    () => (allTags ?? []).map((t) => ({ value: String(t.id), label: t.tag })),
    [allTags]
  )
  const knownIds = useMemo(
    () => new Set(options.map((o) => o.value)),
    [options]
  )

  const [selected, setSelected] = useState<string[]>(
    currentTags.map((t) => String(t.id))
  )
  const [busy, setBusy] = useState(false)

  const applyTagAction = (items: number[], action: 'add' | 'delete') =>
    api.commonManageDealTags({
      values: {
        items,
        action,
        include_ids: [dealId],
        exclude_ids: [],
        filter_params: {},
      },
    })

  const save = async () => {
    setBusy(true)
    try {
      const finalIds: number[] = []
      for (const val of selected) {
        if (knownIds.has(val)) {
          finalIds.push(Number(val))
          continue
        }
        // A typed-in value that isn't an existing tag — reuse a case-insensitive
        // match if there is one, otherwise create the tag first.
        const existing = (allTags ?? []).find(
          (t) => t.tag.toLowerCase() === val.toLowerCase()
        )
        if (existing) {
          finalIds.push(existing.id)
        } else {
          const created = await api.postTag({ tag: val }, 'deals')
          if (created?.id) finalIds.push(created.id)
        }
      }

      const originalIds = currentTags.map((t) => t.id)
      const toAdd = finalIds.filter((id) => !originalIds.includes(id))
      const toRemove = originalIds.filter((id) => !finalIds.includes(id))

      if (toAdd.length) await applyTagAction(toAdd, 'add')
      if (toRemove.length) await applyTagAction(toRemove, 'delete')

      toast.success('Tags updated')
      onSaved()
      onOpenChange(false)
    } catch {
      toast.error('Failed to update tags')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <MultiCombobox
          options={options}
          value={selected}
          onChange={setSelected}
          allowsCustomValue
          placeholder="Search or create a tag…"
          aria-label="Deal tags"
          emptyMessage="No tags found"
        />
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy}>
          Save tags
        </Button>
      </DialogFooter>
    </>
  )
}

// ─── Primary-company / locations modal (fb-ui) ────────────────────────────────

/** fb-ui Dialog for editing which locations a deal applies to — an
 *  entire-company / select-locations radio plus the shared fb-ui door picker
 *  (the same flow the new Add/Edit Contact modal uses). Modeled on the contact
 *  detail page's company edit dialog. The deal's company itself isn't
 *  reassignable (no backend field), so this is scope-only. */
function EditDealLocationsModalFbui({
  open,
  onOpenChange,
  deal,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  deal: DealDetail
  onSaved: () => void
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-3xl">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Edit locations</DialogTitle>
          </DialogHeader>
          <EditDealLocationsBody
            key={open ? 'open' : 'closed'}
            deal={deal}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

/** Fetches the deal's current door selection (when scoped) before mounting the
 *  form, so the picker seeds from the saved state without an effect. */
function EditDealLocationsBody({
  deal,
  onOpenChange,
  onSaved,
}: {
  deal: DealDetail
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const shouldFetch = deal.apply_to === 'select_locations'
  const { data: doorIdsData, isLoading } = api.useGetSelectedDoorIdsForDeal(
    deal.id,
    shouldFetch,
    { noCache: true }
  )
  const ready = !shouldFetch || !isLoading

  if (!ready) {
    return (
      <DialogBody>
        <div className="flex flex-col gap-2 py-8">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-40 w-full" />
        </div>
      </DialogBody>
    )
  }

  const initialDoorIds = shouldFetch ? (doorIdsData?.ids ?? []) : []
  return (
    <EditDealLocationsForm
      key={`${deal.id}-${initialDoorIds.length}`}
      deal={deal}
      initialDoorIds={initialDoorIds}
      onOpenChange={onOpenChange}
      onSaved={onSaved}
    />
  )
}

function EditDealLocationsForm({
  deal,
  initialDoorIds,
  onOpenChange,
  onSaved,
}: {
  deal: DealDetail
  initialDoorIds: number[]
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const company = deal.contact_company
  const [applyTo, setApplyTo] = useState<ApplyTo>(deal.apply_to)
  const [doorIds, setDoorIds] = useState<number[]>(initialDoorIds)
  const [busy, setBusy] = useState(false)

  const singleDoorCompany = (company?.total_doors ?? 0) <= 1
  const canSave = applyTo === 'entire_company' || doorIds.length > 0

  const save = async () => {
    setBusy(true)
    try {
      const body: Partial<DealEdit> = { apply_to: applyTo }
      if (applyTo === 'select_locations') body.door_ids = doorIds
      await api.editDeal(deal.id, body)
      toast.success('Locations updated')
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update locations'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <div className="flex flex-col gap-4">
          {company ? (
            <p className="text-sm text-fg-secondary">
              Company:{' '}
              <span className="font-medium text-fg">{company.name}</span>
            </p>
          ) : null}
          <RadioGroup
            value={applyTo}
            onValueChange={(v) => setApplyTo(v as ApplyTo)}
            aria-label="Apply this deal to"
            className="flex flex-col gap-3 sm:flex-row sm:gap-6"
          >
            <Radio value="entire_company" label="Entire company" />
            <Radio
              value="select_locations"
              label="Select locations"
              disabled={singleDoorCompany}
            />
          </RadioGroup>
          {applyTo === 'select_locations' && company ? (
            <SelectLocationsPanelFbui
              chainProxyId={company.id}
              companyTotalDoors={company.total_doors}
              selectedIds={doorIds}
              onSelectedIdsChange={setDoorIds}
            />
          ) : null}
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy || !canSave}>
          Save
        </Button>
      </DialogFooter>
    </>
  )
}

// ─── Related companies modal (fb-ui) ──────────────────────────────────────────

type RelatedCompanyItem = {
  id: number
  name: string
  sub_category?: {
    id: number
    name: string
    slug: string
    description: string
  } | null
  url?: string
}

/** fb-ui Dialog for editing a deal's related companies — a server-searched
 *  company picker to add, a removable list of the current set, diffed on save.
 *  Mirrors the contact detail page's company-edit dialog chrome. */
function EditRelatedCompaniesModalFbui({
  open,
  onOpenChange,
  primaryCompanyId,
  currentRelatedCompanies,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  primaryCompanyId: number
  currentRelatedCompanies: RelatedCompanyItem[]
  onSave: (added: number[], removed: number[]) => Promise<void>
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-md">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Related companies</DialogTitle>
          </DialogHeader>
          {/* Keyed on `open` so the working list reseeds each time it opens. */}
          <RelatedCompaniesModalBody
            key={open ? 'open' : 'closed'}
            primaryCompanyId={primaryCompanyId}
            currentRelatedCompanies={currentRelatedCompanies}
            onOpenChange={onOpenChange}
            onSave={onSave}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

function RelatedCompaniesModalBody({
  primaryCompanyId,
  currentRelatedCompanies,
  onOpenChange,
  onSave,
}: {
  primaryCompanyId: number
  currentRelatedCompanies: RelatedCompanyItem[]
  onOpenChange: (open: boolean) => void
  onSave: (added: number[], removed: number[]) => Promise<void>
}) {
  const api = useApiService()
  const [companies, setCompanies] = useState<RelatedCompanyItem[]>([
    ...currentRelatedCompanies,
  ])
  const [search, setSearch] = useState('')
  // Debounce the query so typing a chain name (e.g. "starbucks") doesn't fire a
  // large-payload search per keystroke and churn the combobox. The input stays
  // responsive via `search`; only the query waits on `debounced`.
  const [debounced] = useDebounce(search, 400)
  const [busy, setBusy] = useState(false)
  // Bumped on each add to remount the Combobox — react-aria keeps the popover
  // open after a pick when `selectedKey` is pinned null (a never-"selected"
  // add-to-list picker), so a fresh mount is the clean way to close + clear it
  // and reveal the newly added row below.
  const [comboKey, setComboKey] = useState(0)

  const { data: results, isFetching } = useQuery({
    queryKey: ['companies_list_search', debounced],
    queryFn: ({ signal }) =>
      api.searchCompaniesList({ search: debounced }, signal),
    enabled: debounced.trim().length > 1,
    retry: false,
  })

  // Existing/primary ids are filtered out so you can't add a duplicate or the
  // deal's own primary company.
  const existingIds = useMemo(
    () => new Set([primaryCompanyId, ...companies.map((c) => c.id)]),
    [primaryCompanyId, companies]
  )

  const options = useMemo(
    () =>
      (results ?? [])
        .filter(
          (r) =>
            r.contact_company_id != null &&
            !existingIds.has(r.contact_company_id)
        )
        .map((r) => {
          const source =
            r.is_unmatched != null
              ? r.is_unmatched
                ? 'Custom'
                : 'First Bite'
              : undefined
          const isChain = (r.location_count ?? 0) > 1
          const address = [r.address_line1, r.city, r.state]
            .filter(Boolean)
            .join(', ')
          const scope = isChain
            ? `Chain · ${(r.location_count as number).toLocaleString()} locations`
            : address
              ? `Location · ${address}`
              : 'Location'
          const meta = [source, r.category_name, scope]
            .filter(Boolean)
            .join(' · ')
          return {
            id: r.contact_company_id as number,
            name: r.name,
            sub_category_name: r.sub_category_name,
            sub_category: r.sub_category ?? 0,
            meta,
          }
        }),
    [results, existingIds]
  )

  const emptyMessage =
    search.trim().length <= 1
      ? 'Type to search companies'
      : isFetching || search !== debounced
        ? 'Searching…'
        : 'No companies found'

  const addCompany = (id: number) => {
    const opt = options.find((o) => o.id === id)
    if (!opt) return
    setCompanies((prev) => [
      ...prev,
      {
        id: opt.id,
        name: opt.name,
        sub_category: opt.sub_category_name
          ? {
              id: opt.sub_category,
              name: opt.sub_category_name,
              slug: '',
              description: '',
            }
          : null,
      },
    ])
    setSearch('')
    setComboKey((k) => k + 1)
  }

  const removeCompany = (id: number) =>
    setCompanies((prev) => prev.filter((c) => c.id !== id))

  const save = async () => {
    const originalIds = new Set(currentRelatedCompanies.map((c) => c.id))
    const nextIds = new Set(companies.map((c) => c.id))
    const added = companies
      .filter((c) => !originalIds.has(c.id))
      .map((c) => c.id)
    const removed = currentRelatedCompanies
      .filter((c) => !nextIds.has(c.id))
      .map((c) => c.id)

    if (added.length === 0 && removed.length === 0) {
      onOpenChange(false)
      return
    }
    setBusy(true)
    try {
      await onSave(added, removed)
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <Combobox
            key={comboKey}
            aria-label="Add related company"
            menuTrigger="input"
            allowsEmptyCollection
            inputValue={search}
            onInputChange={setSearch}
            selectedKey={null}
            onSelectionChange={(key) => key != null && addCompany(Number(key))}
          >
            <ComboboxInput placeholder="Search companies to add…" />
            <ComboboxContent emptyMessage={emptyMessage}>
              {options.map((o) => (
                <ComboboxItem key={o.id} id={o.id} textValue={o.name}>
                  <span className="flex flex-col">
                    <span>{o.name}</span>
                    {o.meta ? (
                      <span className="text-xs text-fg-secondary">
                        {o.meta}
                      </span>
                    ) : null}
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxContent>
          </Combobox>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-secondary">
              Related companies ({companies.length})
            </span>
            {companies.length === 0 ? (
              <p className="py-4 text-center text-sm text-fg-secondary">
                No related companies yet. Search above to add one.
              </p>
            ) : (
              <div className="fbui-scroll flex max-h-64 flex-col overflow-y-auto">
                {companies.map((c) => (
                  // Single, coherent color story: a calm neutral hover for the
                  // row generally, but when the trash is hovered the whole row
                  // tints red (`has-[button:hover]`) — a delete preview — so the
                  // destructive intent reads as one signal, not a green row
                  // fighting a red button.
                  <div
                    key={c.id}
                    className="group/rc flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-surface-alt has-[button:hover]:bg-error-bg-subtle"
                  >
                    <PiBuildings className="shrink-0 text-fg-tertiary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-fg">
                        {c.name}
                      </p>
                      {c.sub_category ? (
                        <p className="truncate text-xs text-fg-secondary">
                          {c.sub_category.name}
                        </p>
                      ) : null}
                    </div>
                    {/* Trash icon reddens on hover; its own bg stays transparent
                        so the row's red preview shows through cleanly (the `!`
                        overrides the fb-ui neutral variant's gray icon-tone). */}
                    <Button
                      variant="neutral"
                      iconOnly
                      size="sm"
                      aria-label={`Remove ${c.name}`}
                      onClick={() => removeCompany(c.id)}
                      className="hover:!bg-transparent hover:!text-error-strong"
                    >
                      <PiTrash />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy}>
          Save changes
        </Button>
      </DialogFooter>
    </>
  )
}

// ─── Product modal (fb-ui) ────────────────────────────────────────────────────

/** fb-ui Dialog for changing a deal's product + variant — a searchable product
 *  Combobox and a variant Combobox scoped to the chosen product. Replaces the
 *  legacy ds2026 product modal. */
function EditDealProductModalFbui({
  open,
  onOpenChange,
  dealId,
  currentProductId,
  currentProductVariantId,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  dealId: number
  currentProductId: number | null
  currentProductVariantId: number | null
  onSaved: () => void
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-md">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Edit product</DialogTitle>
          </DialogHeader>
          <ProductModalBody
            key={open ? 'open' : 'closed'}
            dealId={dealId}
            currentProductId={currentProductId}
            currentProductVariantId={currentProductVariantId}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

function ProductModalBody({
  dealId,
  currentProductId,
  currentProductVariantId,
  onOpenChange,
  onSaved,
}: {
  dealId: number
  currentProductId: number | null
  currentProductVariantId: number | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const { data: productOptions, isLoading: optionsLoading } =
    api.useGetProductsOptions()
  const { data: products, isLoading: productsLoading } = api.useGetProducts()
  const ready = !optionsLoading && !productsLoading

  if (!ready) {
    return (
      <DialogBody>
        <div className="flex flex-col gap-3 py-6">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </DialogBody>
    )
  }

  return (
    <ProductModalForm
      dealId={dealId}
      currentProductId={currentProductId}
      currentProductVariantId={currentProductVariantId}
      productOptions={productOptions ?? []}
      products={products ?? []}
      onOpenChange={onOpenChange}
      onSaved={onSaved}
    />
  )
}

function ProductModalForm({
  dealId,
  currentProductId,
  currentProductVariantId,
  productOptions,
  products,
  onOpenChange,
  onSaved,
}: {
  dealId: number
  currentProductId: number | null
  currentProductVariantId: number | null
  productOptions: Array<{ label: string; value: number }>
  products: Array<{
    id: number
    name: string
    variants?: Array<{ id?: number; name: string; sku?: string }>
  }>
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()

  const [productId, setProductId] = useState<number | null>(currentProductId)
  const [variantId, setVariantId] = useState<number | null>(
    currentProductVariantId
  )
  const initialProductLabel =
    productOptions.find((o) => o.value === currentProductId)?.label ?? ''
  const [productSearch, setProductSearch] = useState(initialProductLabel)
  const [busy, setBusy] = useState(false)

  const variants = useMemo(
    () =>
      (products.find((p) => p.id === productId)?.variants ?? []).filter(
        (v): v is { id: number; name: string; sku?: string } => v.id != null
      ),
    [products, productId]
  )
  const initialVariantLabel =
    variants.find((v) => v.id === currentProductVariantId)?.name ?? ''
  const [variantSearch, setVariantSearch] = useState(initialVariantLabel)

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    if (!q) return productOptions
    return productOptions.filter((o) => o.label.toLowerCase().includes(q))
  }, [productOptions, productSearch])

  const filteredVariants = useMemo(() => {
    const q = variantSearch.trim().toLowerCase()
    if (!q) return variants
    return variants.filter((v) => v.name.toLowerCase().includes(q))
  }, [variants, variantSearch])

  const save = async () => {
    if (productId == null) return
    setBusy(true)
    try {
      await api.editDeal(dealId, {
        product_id: productId,
        product_variant_id: variantId,
      })
      toast.success('Product updated')
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update product'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-fg-secondary">
              Product
            </span>
            <Combobox
              aria-label="Product"
              menuTrigger="focus"
              allowsEmptyCollection
              inputValue={productSearch}
              onInputChange={setProductSearch}
              selectedKey={productId}
              onSelectionChange={(key) => {
                if (key == null) return
                const next = Number(key)
                setProductId(next)
                setVariantId(null)
                setVariantSearch('')
                setProductSearch(
                  productOptions.find((o) => o.value === next)?.label ?? ''
                )
              }}
            >
              <ComboboxInput placeholder="Select product…" />
              <ComboboxContent emptyMessage="No products found">
                {filteredProducts.map((o) => (
                  <ComboboxItem key={o.value} id={o.value} textValue={o.label}>
                    {o.label}
                  </ComboboxItem>
                ))}
              </ComboboxContent>
            </Combobox>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-fg-secondary">
              Variant <span className="text-fg-tertiary">(optional)</span>
            </span>
            <Combobox
              aria-label="Variant"
              menuTrigger="focus"
              allowsEmptyCollection
              disabled={productId == null || variants.length === 0}
              inputValue={variantSearch}
              onInputChange={setVariantSearch}
              selectedKey={variantId}
              onSelectionChange={(key) => {
                if (key == null) return
                const next = Number(key)
                setVariantId(next)
                setVariantSearch(
                  variants.find((v) => v.id === next)?.name ?? ''
                )
              }}
            >
              <ComboboxInput
                placeholder={
                  variants.length === 0 ? 'No variants' : 'Select variant…'
                }
              />
              <ComboboxContent emptyMessage="No variants found">
                {filteredVariants.map((v) => (
                  <ComboboxItem key={v.id} id={v.id} textValue={v.name}>
                    <span className="flex flex-col">
                      <span>{v.name}</span>
                      {v.sku ? (
                        <span className="text-xs tabular-nums text-fg-secondary">
                          {v.sku}
                        </span>
                      ) : null}
                    </span>
                  </ComboboxItem>
                ))}
              </ComboboxContent>
            </Combobox>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy || productId == null}>
          Save
        </Button>
      </DialogFooter>
    </>
  )
}
